import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { Trans } from "@lingui/macro";
import SEO from "components/Common/SEO";
import Footer from "components/Footer/Footer";
import { getPageTitle } from "lib/legacy";
import ExternalLink from "components/ExternalLink/ExternalLink";
import "./DocsPage.css";

// ─── Section Registry ─────────────────────────────────────────────────────────

type SectionDef = {
  id: string;
  label: string;
  emoji: string;
  desc: string;
  content: () => JSX.Element;
};

const SECTIONS: SectionDef[] = [
  { id: "introduction",    label: "Introduction",       emoji: "🏠", desc: "What is MoneyX?",                content: SIntroduction },
  { id: "besc-network",    label: "BESC Network",       emoji: "🌐", desc: "Chain details, gas, bridges",    content: SBescNetwork },
  { id: "trading-guide",   label: "Trading Guide",      emoji: "📈", desc: "How to open & close positions",  content: STradingGuide },
  { id: "order-types",     label: "Order Types",        emoji: "📋", desc: "Market, limit, TP/SL at open, TWAP, Scale", content: SOrderTypes },
  { id: "fees",            label: "Fees",               emoji: "💸", desc: "All platform fees explained",    content: SFees },
  { id: "fee-distribution",label: "Fee Distribution",   emoji: "📊", desc: "Where every fee goes",           content: SFeeDistribution },
  { id: "liquidation",     label: "Liquidation",        emoji: "⚠️", desc: "How liquidation works",          content: SLiquidation },
  { id: "risk-management", label: "Risk Management",    emoji: "🛡️", desc: "Position sizing & risk control", content: SRiskManagement },
  { id: "liquidity",       label: "Liquidity (MLP)",    emoji: "💧", desc: "Provide liquidity & earn fees",  content: SLiquidity },
  { id: "staking",         label: "Staking",            emoji: "🔒", desc: "Stake MONEY & earn rewards",     content: SStaking },
  { id: "vesting",         label: "Vesting (esMONEY)",  emoji: "⏳", desc: "Convert esMONEY → MONEY",        content: SVesting },
  { id: "copy-trading",    label: "Copy Trading",       emoji: "🤝", desc: "Follow top traders automatically", content: SCopyTrading },
  { id: "referrals",       label: "Referrals",          emoji: "🔗", desc: "Earn from every trader you refer", content: SReferrals },
  { id: "synthetics",      label: "Synthetics",         emoji: "🔮", desc: "Synthetic markets & stocks",     content: SSynthetics },
  { id: "contracts",       label: "Contracts",          emoji: "📜", desc: "All deployed contract addresses", content: SContracts },
  { id: "notifications",   label: "Notifications",      emoji: "🔔", desc: "Price alerts & position monitoring", content: SNotifications },
  { id: "api-automation",  label: "API & Automation",   emoji: "🤖", desc: "Build bots & integrations",      content: SApiAutomation },
  { id: "speed-up",        label: "Speed Up",           emoji: "⚡", desc: "Fix loading & RPC tips",         content: SSpeedUp },
  { id: "faq",             label: "FAQ",                emoji: "❓", desc: "Common questions answered",      content: SFAQ },
];

// ─── Page Shell ───────────────────────────────────────────────────────────────

export function DocsPage() {
  const { section } = useParams<{ section?: string }>();
  const history = useHistory();
  const [activeId, setActiveId] = useState(
    SECTIONS.find((s) => s.id === section)?.id ?? "introduction"
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = SECTIONS.find((s) => s.id === section)?.id;
    if (id) setActiveId(id);
    else setActiveId("introduction");
  }, [section]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeId]);

  const activeSec = SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0];
  const Content = activeSec.content;
  const activeIdx = SECTIONS.findIndex((s) => s.id === activeId);
  const prevSec = activeIdx > 0 ? SECTIONS[activeIdx - 1] : null;
  const nextSec = activeIdx < SECTIONS.length - 1 ? SECTIONS[activeIdx + 1] : null;

  const navigate = (id: string) => {
    setActiveId(id);
    setMobileMenuOpen(false);
    history.replace(`/docs/${id}`);
  };

  return (
    <SEO title={getPageTitle(`Docs — ${activeSec.label}`)}>
      <div className="Docs-page default-container page-layout">
        <div className="Docs-layout">

          {/* ── Sidebar ─────────────────────────────────────────────── */}
          <aside className={`Docs-sidebar ${mobileMenuOpen ? "open" : ""}`}>
            <div className="Docs-sidebar-header">
              <span>Documentation</span>
              <button className="Docs-sidebar-close" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>
            <nav className="Docs-nav">
              {SECTIONS.map((s, i) => (
                <button
                  key={s.id}
                  className={`Docs-nav-item ${activeId === s.id ? "active" : ""}`}
                  onClick={() => navigate(s.id)}
                >
                  <span className="Docs-nav-emoji">{s.emoji}</span>
                  <span className="Docs-nav-label">{s.label}</span>
                  {activeId === s.id && <span className="Docs-nav-bar" />}
                </button>
              ))}
              <Link
                className="Docs-nav-item Docs-nav-link"
                to="/health"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="Docs-nav-emoji">●</span>
                <span className="Docs-nav-label">Network Health</span>
              </Link>
            </nav>
          </aside>

          {mobileMenuOpen && (
            <div className="Docs-sidebar-backdrop" onClick={() => setMobileMenuOpen(false)} />
          )}

          {/* ── Main content ─────────────────────────────────────────── */}
          <main className="Docs-content" ref={contentRef}>

            {/* Mobile top bar */}
            <button className="Docs-mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
              <span className="Docs-mobile-menu-emoji">{activeSec.emoji}</span>
              <span>{activeSec.label}</span>
              <span className="Docs-mobile-menu-arrow">☰</span>
            </button>

            {/* Breadcrumb */}
            <div className="Docs-breadcrumb">
              <Link to="/docs/introduction">Docs</Link>
              <span className="Docs-breadcrumb-sep">›</span>
              <span>{activeSec.label}</span>
            </div>

            {/* Article body */}
            <article className="Docs-article">
              <Content />
            </article>

            {/* Prev / Next */}
            <div className="Docs-pagination">
              {prevSec ? (
                <button className="Docs-pager Docs-pager-prev" onClick={() => navigate(prevSec.id)}>
                  <span className="Docs-pager-dir">← Previous</span>
                  <span className="Docs-pager-title">{prevSec.emoji} {prevSec.label}</span>
                </button>
              ) : <span />}
              {nextSec ? (
                <button className="Docs-pager Docs-pager-next" onClick={() => navigate(nextSec.id)}>
                  <span className="Docs-pager-dir">Next →</span>
                  <span className="Docs-pager-title">{nextSec.emoji} {nextSec.label}</span>
                </button>
              ) : <span />}
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </SEO>
  );
}

// ─── Shared UI Primitives ─────────────────────────────────────────────────────

function Callout({ children, type = "info" }: { children: React.ReactNode; type?: "info" | "warn" | "tip" }) {
  const icons = { info: "ℹ️", warn: "⚠️", tip: "💡" };
  return (
    <div className={`Docs-callout Docs-callout-${type}`}>
      <span className="Docs-callout-icon">{icons[type]}</span>
      <span>{children}</span>
    </div>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  return <pre className="Docs-code-block">{children}</pre>;
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="Docs-step">
      <div className="Docs-step-num">{n}</div>
      <div className="Docs-step-body">{children}</div>
    </div>
  );
}

function DefTable({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <div className="Docs-def-table">
      {rows.map(([term, def]) => (
        <div key={term} className="Docs-def-row">
          <dt>{term}</dt>
          <dd>{def}</dd>
        </div>
      ))}
    </div>
  );
}

function DataTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="Docs-table-wrap">
      <table className="Docs-table">
        <thead>
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FAQItem({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`Docs-faq-item ${open ? "open" : ""}`}>
      <button className="Docs-faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className="Docs-faq-chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="Docs-faq-a">{children}</div>}
    </div>
  );
}

// ─── Section: Introduction ────────────────────────────────────────────────────

function SIntroduction() {
  return (
    <>
      <h1>Welcome to MoneyX</h1>
      <p className="Docs-lead">
        MoneyX is a decentralized perpetual exchange built on <strong>BESC Hyperchain</strong>. Trade crypto
        directly from your wallet — no sign-up, no KYC, zero counterparty risk.
      </p>
      <Callout type="info">
        <strong>Leverage scales with liquidity.</strong> Maximum leverage is currently <strong>20×</strong> and
        will be raised in steps toward <strong>100×</strong> as the MLP liquidity pool grows. This is deliberate —
        keeping the leverage cap in line with the liquidity the pool can safely back is a core part of what keeps
        MLP rock-solid as it scales, rather than repeating the price bleed that hurt the old v1 pool.
      </Callout>

      <h2>Why MoneyX?</h2>
      <div className="Docs-feature-grid">
        {[
          ["📈", "Leverage That Scales", "Up to 20× today, rising toward 100× as MLP liquidity grows. Long or short major crypto assets."],
          ["🔮", "Oracle Pricing", "Zero price impact — trades execute at real market oracle prices."],
          ["💧", "Earn as LP", "Provide liquidity via MLP and earn 65% of all platform trading fees."],
          ["🔒", "Stake & Earn", "Stake MONEY tokens and earn esMONEY + BUSDC fee rewards."],
          ["🤝", "Copy Trading", "Automatically mirror the positions of top-performing traders."],
          ["🔗", "Referrals", "Earn a share of trading fees from every trader you bring in."],
        ].map(([emoji, title, body]) => (
          <div key={title as string} className="Docs-feature-card">
            <div className="Docs-feature-emoji">{emoji}</div>
            <div className="Docs-feature-title">{title}</div>
            <div className="Docs-feature-body">{body}</div>
          </div>
        ))}
      </div>

      <h2>The Token Ecosystem</h2>
      <DefTable rows={[
        ["MONEY", "The governance and utility token. Stake to earn rewards and boost your yield."],
        ["esMONEY", "Escrowed MONEY — earned as staking rewards. Vest over 1 year to convert to real MONEY."],
        ["MLP", "Liquidity provider token. Buy MLP to provide liquidity and earn 65% of all fees."],
        ["BUSDC", "The primary collateral and settlement token on BESC Hyperchain."],
        ["BESC", "The native gas token of BESC Hyperchain, used to pay transaction fees."],
      ]} />

      <h2>How the Protocol Works</h2>
      <p>
        Traders open leveraged positions by depositing collateral (BUSDC). Prices come from trusted oracles so your
        entry and exit are at fair market value with no slippage on the position itself. A small spread, borrowing fee,
        and open/close fee apply.
      </p>
      <p>
        The other side of every trade is the MLP pool — a vault of real on-chain assets (WBESC, MONEY, BUSDC)
        deposited by liquidity providers. When traders lose, MLP holders gain. When traders profit, MLP absorbs
        the loss. In return, MLP holders earn 65% of every fee generated. Markets like BTC, ETH, SOL, AAPL, etc.
        are <em>synthetic indices</em> — the pool prices and settles them in BUSDC without holding the underlying.
      </p>

      <Callout type="tip">
        New here? Start with the <strong>Trading Guide</strong> to open your first position in under 5 minutes.
      </Callout>

      <h2>BESC Hyperchain</h2>
      <CodeBlock>{`Network Name:  BESC Hyperchain
RPC URL:       https://rpc.beschyperchain.com
Chain ID:      2372
Symbol:        BESC
Explorer:      https://explorer.beschyperchain.com`}</CodeBlock>
    </>
  );
}

// ─── Section: Trading Guide ────────────────────────────────────────────────────

function STradingGuide() {
  return (
    <>
      <h1>Trading Guide</h1>
      <p className="Docs-lead">
        Everything you need to go from zero to your first leveraged trade on MoneyX.
      </p>

      <h2>Before You Start</h2>
      <p>You need two things:</p>
      <ul>
        <li><strong>BESC</strong> — for gas fees (a few cents worth covers many transactions)</li>
        <li><strong>BUSDC</strong> — as collateral for leveraged positions</li>
      </ul>
      <p>
        Visit the <Link to="/buy">Buy page</Link> to acquire both, or bridge from another chain.
      </p>

      <h2>Step-by-Step: Open a Long Position</h2>
      <Step n={1}>
        <strong>Connect your wallet.</strong> Click <strong>Connect Wallet</strong> in the top right.
        Select MetaMask, WalletConnect, or any EVM wallet. Add BESC Hyperchain if prompted
        (Chain ID 2372, RPC: <code>https://rpc.beschyperchain.com</code>).
      </Step>
      <Step n={2}>
        <strong>Navigate to Trade.</strong> Click <strong>Trade</strong> in the top nav. You land on the V1
        exchange (the default perpetuals platform on BESC).
      </Step>
      <Step n={3}>
        <strong>Pick an asset.</strong> Click the token selector next to the chart (e.g., BTC/USD). MoneyX
        lists 30+ markets: WBESC, MONEY (native), the major crypto synths (BTC, ETH, BNB, SOL, XRP, DOGE,
        ADA, TRX, LINK, AVAX, SHIB, TON, DOT, NEAR, MATIC, LTC, UNI, ATOM, ARB, OP), and tokenised stocks
        (AAPL, TSLA, NVDA, MSFT, GOOGL, AMZN, META, SPY, QQQ, GOLD — only tradeable during NYSE / NASDAQ hours).
      </Step>
      <Step n={4}>
        <strong>Choose Long or Short.</strong>
        <ul>
          <li><strong>Long</strong> — you profit if price goes up</li>
          <li><strong>Short</strong> — you profit if price goes down</li>
        </ul>
      </Step>
      <Step n={5}>
        <strong>Enter collateral and leverage.</strong> Type your collateral amount in BUSDC and use the
        leverage slider (1× – 20× at current liquidity; the cap rises toward 100× as the MLP pool grows).
        The position size, liquidation price, and fees update in real time.
      </Step>
      <Step n={6}>
        <strong>Review and confirm.</strong> Check the entry price, fees, and liquidation price.
        Click <strong>Long ETH</strong> (or whatever asset/direction). Approve in your wallet.
      </Step>
      <Step n={7}>
        <strong>Wait for execution.</strong> Orders go through a keeper bot that executes them on-chain
        within seconds. Your position appears in the <strong>Positions</strong> tab once filled.
      </Step>

      <Callout type="info">
        Market orders have a <strong>30-second execution window</strong>. If the oracle price moves more than
        the allowed slippage before the keeper executes, the order is cancelled and your collateral returned.
      </Callout>

      <h2>Reading the Position Panel</h2>
      <DefTable rows={[
        ["Size", "Total position size = collateral × leverage"],
        ["Collateral", "Your deposited margin in USD"],
        ["Entry Price", "The oracle price at time of fill"],
        ["Mark Price", "Current oracle price used to calculate P&L"],
        ["Liq. Price", "Price at which your position gets liquidated"],
        ["PnL", "Unrealised profit or loss at current mark price (after fees)"],
        ["Borrow Fee", "Accumulated funding fee so far on this position"],
      ]} />

      <h2>Managing an Open Position</h2>
      <p>In the Positions tab, each row has action buttons:</p>
      <ul>
        <li><strong>Close</strong> — close all or part of the position at market price</li>
        <li><strong>Edit (pencil)</strong> — add or remove collateral to adjust leverage and liquidation price</li>
        <li><strong>TP/SL (dropdown)</strong> — set take-profit, stop-loss, or trailing stop orders</li>
        <li><strong>Share</strong> — generate a shareable PnL card image</li>
      </ul>

      <h2>Closing a Position</h2>
      <Step n={1}>Click <strong>Close</strong> on the position row.</Step>
      <Step n={2}>Enter a size to close (or leave at 100% for full close).</Step>
      <Step n={3}>Review the exit price and net P&L.</Step>
      <Step n={4}>Confirm in your wallet. Funds arrive in your wallet within seconds.</Step>

      <Callout type="warn">
        Always set a stop-loss. Leveraged positions can be liquidated within minutes if the market moves sharply
        against you. A stop-loss at –20% prevents total loss of collateral.
      </Callout>

      <h2>Partial Closes</h2>
      <p>
        You can close any portion of your position. For example, close 50% to lock in profit while keeping
        upside exposure. The remaining position stays open with the same leverage ratio.
      </p>

      <h2>Adjusting Collateral</h2>
      <p>
        Click the <strong>Edit</strong> button on a position. You can:
      </p>
      <ul>
        <li><strong>Deposit more collateral</strong> → reduces leverage → moves liquidation price further away</li>
        <li><strong>Withdraw collateral</strong> → increases leverage → moves liquidation price closer</li>
      </ul>
      <p>
        Withdrawing collateral that would push leverage above 49× is blocked to prevent instant liquidation.
      </p>
    </>
  );
}

// ─── Section: Order Types ─────────────────────────────────────────────────────

function SOrderTypes() {
  return (
    <>
      <h1>Order Types</h1>
      <p className="Docs-lead">
        MoneyX supports a full range of order types: market, limit, take-profit/stop-loss (which you can now
        attach at the moment you open a position), trailing stop, plus TWAP and Scale execution for larger
        orders. Understanding each one lets you trade with precision and manage risk automatically.
      </p>

      <h2>Market Orders</h2>
      <p>
        A market order opens or closes your position immediately at the next available oracle price.
        It's the fastest way to enter or exit.
      </p>
      <DefTable rows={[
        ["Execution", "Next keeper execution (usually within 5–15 seconds)"],
        ["Price", "Oracle price at time of execution ± allowed slippage"],
        ["Slippage", "Configurable — default 0.3%. Increase in volatile markets"],
        ["Cancellation", "Auto-cancelled if oracle price moves outside slippage before execution"],
      ]} />
      <Callout type="tip">
        Increase your allowed slippage in Settings during high volatility to avoid order cancellations.
      </Callout>

      <h2>Limit Orders</h2>
      <p>
        A limit order executes only when the oracle mark price reaches a price you specify. Use it to enter
        a position at a better price than the current market, or to take profit at a target.
      </p>
      <ul>
        <li><strong>Long Limit</strong>: triggers when price falls to or below your limit price</li>
        <li><strong>Short Limit</strong>: triggers when price rises to or above your limit price</li>
      </ul>
      <p>
        Limit orders live on-chain indefinitely until filled or cancelled. There is a small execution fee
        in BESC that is refunded if the order is cancelled.
      </p>
      <Callout type="info">
        Limit orders use the <strong>oracle mark price</strong>, not the index price or last trade price.
        The mark price is an aggregate of multiple price feeds and is resistant to manipulation.
      </Callout>

      <h2>Take-Profit (TP)</h2>
      <p>
        A take-profit order automatically closes your position once the mark price reaches your profit target.
      </p>
      <ul>
        <li>For a <strong>Long</strong>: TP triggers when price rises above your target</li>
        <li>For a <strong>Short</strong>: TP triggers when price falls below your target</li>
      </ul>
      <p>
        To set a TP: open the position's <strong>TP/SL</strong> menu → enter the price and optionally the
        partial close size (leave at 100% to close fully). Confirm in your wallet.
      </p>

      <h2>Stop-Loss (SL)</h2>
      <p>
        A stop-loss automatically closes your position when the mark price moves against you by a defined amount.
        It is your primary defense against large drawdowns and liquidation.
      </p>
      <ul>
        <li>For a <strong>Long</strong>: SL triggers when price falls below your stop price</li>
        <li>For a <strong>Short</strong>: SL triggers when price rises above your stop price</li>
      </ul>
      <Callout type="warn">
        A stop-loss does <strong>not</strong> guarantee an exact fill price during extreme volatility.
        If the oracle gaps down through your stop, the fill will be at the next available oracle price.
        Set your SL with enough buffer above your liquidation price.
      </Callout>

      <h2>Trailing Stop</h2>
      <p>
        A trailing stop follows the mark price in your favour by a fixed percentage or dollar amount. If the
        market reverses by the trail amount, the position closes automatically — locking in profit.
      </p>
      <p>How it works for a Long position:</p>
      <ul>
        <li>You set a trail of 5%</li>
        <li>Price rises from $40,000 to $50,000 → trail stop moves up to $47,500</li>
        <li>Price then falls to $47,500 → position closes at $47,500</li>
        <li>If price had continued rising to $60,000, stop would be at $57,000</li>
      </ul>
      <p>
        To set a trailing stop: open <strong>TP/SL</strong> on an existing position → switch to the
        <strong> Trailing Stop</strong> tab → enter the trail percentage → confirm.
      </p>

      <h2>Combining TP, SL, and Trailing Stop</h2>
      <p>
        You can have all three active on the same position simultaneously. The first one to trigger will
        close the specified portion of the position. Common strategy:
      </p>
      <ul>
        <li>SL at –15% of collateral to cap max loss</li>
        <li>TP at +50% to take profit at a target</li>
        <li>Trailing stop at 10% to ride a trend and lock in gains</li>
      </ul>

      <h2>Set TP/SL When You Open a Position</h2>
      <p>
        You no longer have to open a position first and then add protection. Right in the trade box, expand{" "}
        <strong>TP/SL</strong> before you confirm — set a take-profit and/or stop-loss and they are created
        automatically the moment your position opens, in the same flow.
      </p>
      <ul>
        <li>
          Enter each level as a <strong>price</strong> or as a <strong>percentage</strong> — e.g. "+50%" for a
          take-profit or "–20%" for a stop-loss — whichever is faster. No need to look up exact prices.
        </li>
        <li>
          The bracket orders attach on open, so the position is protected from the first second without a
          separate transaction later.
        </li>
      </ul>
      <Callout type="tip">
        Setting a stop-loss at open is the single best habit for leveraged trading — you never hold an
        unprotected position, even for a moment.
      </Callout>

      <h2>TWAP Orders</h2>
      <p>
        A <strong>TWAP</strong> (Time-Weighted Average Price) order breaks one large entry into several equal
        market sub-orders executed at a fixed time interval, so you average into the position across a window
        instead of taking the whole size at a single price. This reduces price impact on larger orders.
      </p>
      <Step n={1}>In the trade box, choose <strong>TWAP</strong>.</Step>
      <Step n={2}>Set the total amount, the number of sub-orders, and the interval between them (1 min up to 1 hr).</Step>
      <Step n={3}>Start it — each sub-order fires automatically as a market order at the chosen interval.</Step>
      <Callout type="warn">
        TWAP runs <strong>in your browser</strong>: keep the tab open until every sub-order has executed. If you
        close the tab or lose connection, the remaining sub-orders will not fire.
      </Callout>

      <h2>Scale (Ladder) Orders</h2>
      <p>
        A <strong>Scale</strong> order places several limit orders spread evenly across a price range instead of
        a single limit at one price, so you build the position gradually and achieve an average entry across the
        whole range.
      </p>
      <Step n={1}>Choose <strong>Scale</strong> in the trade box.</Step>
      <Step n={2}>Set the amount to ladder, the start and end price of the range, and the number of orders (default 5).</Step>
      <Step n={3}>Confirm — each rung is placed as its own on-chain limit order and fills as price moves through it.</Step>
      <Callout type="tip">
        Scale orders shine in choppy or ranging markets where you want a better average price than a single
        entry, and to avoid committing all your size at one level.
      </Callout>

      <h2>Orders Tab</h2>
      <p>
        All pending limit, TP, SL, and Scale orders are visible in the <strong>Orders</strong> tab at the bottom
        of the trade page. You can cancel any order from there at any time.
      </p>
    </>
  );
}

// ─── Section: Fees ────────────────────────────────────────────────────────────

function SFees() {
  return (
    <>
      <h1>Fees</h1>
      <p className="Docs-lead">
        MoneyX charges three main fee types: open/close fees, swap fees, and borrowing fees.
        All fees are visible before you confirm any transaction.
      </p>

      <h2>Open / Close Fee</h2>
      <p>
        A <strong>0.1%</strong> fee is charged on the position size when you open or close a leveraged position.
      </p>
      <CodeBlock>{`Example:
  Position size: $10,000
  Open fee:      $10  (0.1%)
  Close fee:     $10  (0.1%)
  Round-trip:    $20  total`}</CodeBlock>
      <p>
        This fee is deducted from your collateral at the time of opening and closing.
      </p>

      <h2>Borrow (Utilisation) Fee</h2>
      <p>
        Open positions accrue a utilisation-based borrowing fee, charged once per 8-hour funding interval
        and proportional to how much of that asset's pool is currently being used by open positions:
      </p>
      <CodeBlock>{`Rate per 8h interval = (reservedAmount / poolAmount) × fundingRateFactor / 1,000,000

On-chain config (verified):
  fundingRateFactor       = 100      (volatile tokens)
  stableFundingRateFactor = 5        (stable tokens — BUSDC)
  fundingInterval         = 8 hours
  ⇒ max rate at 100% util = 0.01% per 8h ≈ 0.03% per day ≈ ~10.95% APR

Example at 50% pool utilisation:
  Rate per 8h:   0.005%
  Position:      $10,000
  Cost per 8h:   $0.50
  Cost per day:  $1.50  (vs adversarial worst case)`}</CodeBlock>
      <p>
        The fee accrues on the position size (not just collateral). It is shown in the position row as an
        accumulating cost and is deducted from your collateral when you close.
      </p>
      <Callout type="warn">
        For long-duration positions, the utilisation fee + the per-side adaptive funding fee (see below)
        erode your collateral over time. High leverage + heavy skew = liquidation even without adverse price
        movement. Monitor your positions regularly.
      </Callout>

      <h2>Per-Side Adaptive Funding Fee (NEW in v2)</h2>
      <p>
        Synthetic and native markets also run an <strong>adaptive funding rate</strong> that charges the
        over-represented OI side a fee proportional to skew, while the under-represented side pays{" "}
        <strong>zero</strong>. This is the v2 fix for the legacy MLP-bleed pattern — full background in the
        <Link to="/docs/liquidity"> Liquidity</Link> section.
      </p>
      <CodeBlock>{`Per-interval rate (dominant side only) =
   adaptiveFundingFactor × |longOI − shortOI| / totalOI

On-chain config (verified, live):
  adaptiveFundingEnabled = true on both Vault and SynthVault
  adaptiveFundingFactor  = 100  (matches utilisation factor — conservative)
  fundingInterval        = 8 hours

Example: BTC market with $800K long OI, $200K short OI (60% skew):
  Long-side rate:   100 × 600K / 1M / 1,000,000 = 0.006% per 8h
  Short-side rate:  0`}</CodeBlock>
      <p>
        The adaptive funding fee accumulates alongside the utilisation fee. Your position row's "Funding"
        column shows the combined accrued cost.
      </p>

      <h2>Swap Fee</h2>
      <p>
        When your <em>Pay-With</em> token differs from the position's required collateral token, an automatic
        swap occurs at vault rates. Base rates are <strong>0.25% for volatile-token swaps</strong> and{" "}
        <strong>0.01% for stable-to-stable swaps</strong>. Dynamic adjustment can rebate down toward 0% when
        the swap improves pool balance, or add up to a 0.5% tax when it worsens balance.
      </p>
      <ul>
        <li><strong>Improves balance (adding an underweight token)</strong>: fee can drop near 0%</li>
        <li><strong>Worsens balance (adding an overweight token)</strong>: fee can climb to ~0.75% in extreme cases</li>
      </ul>

      <h2>Execution Fee</h2>
      <p>
        A small amount of BESC is charged to cover the gas cost of the keeper bot that executes your order on-chain.
        This is estimated and shown before confirmation. It is refunded if an order is cancelled.
      </p>

      <h2>Fee Distribution</h2>
      <DataTable
        headers={["Recipient", "Share", "Token"]}
        rows={[
          ["MLP Liquidity Providers",  "65%", "BUSDC"],
          ["MONEY / esMONEY Stakers", "25%", "BUSDC"],
          ["Admin / Treasury",         "10%", "BUSDC (sent directly to admin wallet)"],
        ]}
      />
      <p>
        Fees accumulate in real time and are distributed as BUSDC. MLP holders receive their share
        automatically in the token they hold (accrues to MLP price). Stakers must claim via the Earn page.
      </p>

      <h2>Referral Discounts</h2>
      <p>
        When a trader uses a referral code, they receive a <strong>5%–10% fee rebate</strong> on their trading
        fees. This rebate comes from the affiliate's 5%–15% commission — the total fee to the protocol stays
        the same. See the <strong>Referrals</strong> section for tier details.
      </p>
    </>
  );
}

// ─── Section: Liquidation ─────────────────────────────────────────────────────

function SLiquidation() {
  return (
    <>
      <h1>Liquidation</h1>
      <p className="Docs-lead">
        Liquidation protects the liquidity pool. When your collateral can no longer cover your position's
        losses and fees, the protocol closes your position automatically.
      </p>

      <h2>When Does Liquidation Happen?</h2>
      <p>Liquidation occurs when:</p>
      <CodeBlock>{`Remaining Collateral < 1% of Position Size

Remaining Collateral = Deposited Collateral
                     − Unrealised Loss
                     − Accumulated Borrow Fees
                     − Liquidation Fee`}</CodeBlock>

      <h2>Liquidation Price</h2>
      <p>
        Your liquidation price is displayed on every open position. It updates in real time as borrow fees
        accumulate. For a <strong>Long</strong> position, liquidation price is below entry. For a
        <strong> Short</strong>, it is above entry.
      </p>
      <p>
        At high leverage, the liquidation price is very close to your entry. Example:
      </p>
      <DataTable
        headers={["Leverage", "Approx. adverse move to liquidation"]}
        rows={[
          ["5×",  "~18%"],
          ["10×", "~9%"],
          ["20×", "~4.5%"],
          ["50×", "~1.8%"],
          ["100×", "~0.9%"],
        ]}
      />
      <Callout type="warn">
        The current maximum leverage is <strong>20×</strong> — roughly a 4.5% adverse move liquidates you.
        As the cap rises toward 100× with pool growth, even sub-1% moves become dangerous. Always use
        stop-loss orders when trading at high leverage.
      </Callout>

      <h2>Liquidation Fee</h2>
      <p>
        When a position is liquidated, a small fee is taken from your remaining collateral to pay the keeper
        bot that executed the liquidation. Any remaining collateral after the fee is returned to your wallet.
      </p>
      <Callout type="info">
        You cannot lose more than your deposited collateral. MoneyX does not have margin calls or debt positions.
      </Callout>

      <h2>How to Avoid Liquidation</h2>
      <ul>
        <li>
          <strong>Use lower leverage</strong> — 5× or 10× gives a much larger price buffer before liquidation.
        </li>
        <li>
          <strong>Set a stop-loss</strong> — close the position at a controlled loss before liquidation is reached.
          Aim to keep your SL at least 20–30% above the liquidation price.
        </li>
        <li>
          <strong>Add collateral</strong> — use Edit Collateral to deposit more margin. This pushes your
          liquidation price further away without changing your position size.
        </li>
        <li>
          <strong>Monitor borrow fees</strong> — for positions held overnight or for days, borrow fees
          accumulate and gradually erode collateral. Close or top-up long-duration positions.
        </li>
        <li>
          <strong>Avoid over-leveraging on volatile assets</strong> — smaller, less liquid assets can move
          5–10% on a single news event. Use conservative leverage on altcoins.
        </li>
      </ul>

      <h2>Partial Position Management</h2>
      <p>
        If your position is profitable, you can withdraw collateral to take partial profit without closing.
        This keeps leverage higher but moves your liquidation price closer — use with caution.
      </p>
    </>
  );
}

// ─── Section: Liquidity (MLP) ─────────────────────────────────────────────────

function SLiquidity() {
  return (
    <>
      <h1>Liquidity — MLP</h1>
      <p className="Docs-lead">
        MLP (MoneyX Liquidity Provider token) represents your share of the protocol vault.
        Buy MLP to become the counterparty to traders and earn 65% of all platform fees.
      </p>

      <h2>What is the MLP Pool?</h2>
      <p>
        The MLP pool is a vault that holds three on-chain assets: <strong>WBESC, MONEY, and BUSDC</strong>.
        It is the liquidity source for all leveraged trades on MoneyX. When traders open positions, they borrow
        from this pool. When traders lose, the pool gains. When traders profit, the pool pays out.
      </p>
      <Callout type="info">
        BTC, ETH, SOL, and the tokenised stocks (AAPL, TSLA, NVDA, etc.) are <strong>synthetic indices</strong>:
        the protocol prices them via oracles and settles PnL in BUSDC. The MLP pool does <em>not</em> hold
        the underlying assets — only WBESC, MONEY, and BUSDC are physically in the vault.
      </Callout>

      <h2>Earning Fees as an MLP Holder</h2>
      <p>
        MLP holders receive <strong>65% of all trading fees</strong> — open/close fees, swap fees, and
        borrow fees — paid out continuously in BUSDC.
      </p>
      <Callout type="tip">
        Fees are distributed in proportion to your MLP balance. The higher your MLP share, the larger
        your portion of the fee stream.
      </Callout>

      <h2>MLP Price</h2>
      <p>
        The MLP price is not fixed — it is calculated from the total USD value of all assets in the pool
        divided by the total MLP supply. As fees accumulate and traders lose, MLP price increases. If
        traders are collectively profitable, MLP price decreases slightly.
      </p>

      <h2>Buying MLP</h2>
      <Step n={1}>Go to <Link to="/earn">Earn → Buy MLP</Link>.</Step>
      <Step n={2}>Select WBESC, MONEY, or BUSDC to deposit (BUSDC recommended for simplest math).</Step>
      <Step n={3}>Enter the amount. Review the MLP received and the swap fee (if any).</Step>
      <Step n={4}>Click <strong>Buy MLP</strong> and confirm in your wallet.</Step>
      <Step n={5}>MLP tokens appear in your wallet immediately. You start earning fees right away.</Step>

      <Callout type="info">
        There is a <strong>15-minute cooldown</strong> after buying MLP before you can sell. This prevents
        flash-loan attacks that would exploit fee distributions.
      </Callout>

      <h2>Selling MLP</h2>
      <Step n={1}>Go to <Link to="/earn">Earn → Sell MLP</Link>.</Step>
      <Step n={2}>Enter the MLP amount to redeem.</Step>
      <Step n={3}>Choose which token you want to receive (WBESC, MONEY, or BUSDC — the three pool assets).</Step>
      <Step n={4}>Review the output amount and any swap fee.</Step>
      <Step n={5}>Confirm. Tokens are sent to your wallet immediately.</Step>

      <Callout type="warn">
        You may not be able to sell all MLP into a specific token if the pool is heavily utilised.
        If one token's pool is near-empty, choose a different token to redeem, or wait for pool rebalancing.
      </Callout>

      <h2>How v2 Protects MLP Price (Fixed v1 Issue)</h2>
      <p>
        MoneyX v1 inherited a well-known LP-bleed pattern from its perpetual-DEX lineage: when one side
        of a market got heavily skewed — say, far more longs than shorts on BTC — the funding rate stayed
        flat for both sides, so winning traders paid almost nothing in carrying cost while the LP pool
        absorbed the entire directional loss with no offsetting revenue.
        Over months this drained MLP from around <strong>$1.00</strong> down to roughly <strong>$0.005</strong>
        on the v1 deployment.
      </p>
      <p>
        <strong>MoneyX v2 fixes this at the protocol level.</strong> A per-side adaptive funding rate now
        runs on every market:
      </p>
      <ul>
        <li>
          When long open interest dominates, <strong>longs pay a funding fee that scales with the OI imbalance</strong>.
          The bigger the skew, the higher the rate.
        </li>
        <li>
          Shorts (the under-represented side) pay <strong>0</strong> — they're incentivised to open and
          rebalance the book.
        </li>
        <li>
          All funding flows to the MLP pool, directly compensating LPs for the directional risk they're taking.
        </li>
        <li>
          As skew persists, the carrying cost forces dominant-side positions to close, restoring balance and
          stopping the bleed before it compounds.
        </li>
      </ul>
      <p>
        The fee math runs through the <code>VaultUtils</code> contract attached to each Vault
        (<a href="https://explorer.beschyperchain.com/address/0x42502D9F103f24094D2c1B5a4CFF33D125B52dc2#code"
            target="_blank" rel="noreferrer">main</a>{" "}/{" "}
        <a href="https://explorer.beschyperchain.com/address/0x049f615fCcc8418E71438F101560217626bE5Dad#code"
            target="_blank" rel="noreferrer">synth</a>), backed by the verified{" "}
        <a href="https://explorer.beschyperchain.com/address/0xFB59481eCA66A291841f7F9860a5feA925e18caB#code"
           target="_blank" rel="noreferrer">AdaptiveFundingLib</a> library. Both are
        publicly verifiable on the BESC explorer.
      </p>

      <Callout type="tip">
        <strong>Leverage grows with the pool.</strong> Maximum leverage is deliberately capped to what current
        MLP liquidity can safely back — <strong>20× today</strong> — and raised in steps toward <strong>100×</strong>{" "}
        as the pool deepens. Together with adaptive funding, this keeps MLP resilient as MoneyX scales, instead
        of letting oversized leverage drain the pool.
      </Callout>

      <h2>Fees Distribute Automatically (Also Fixed in v2)</h2>
      <p>
        A separate v1 pain point was that fee distribution required a manual weekly admin transaction —
        slow, error-prone, and entirely dependent on a single operator showing up. In v2 this is fully
        automated by the on-chain <code>AutoFeeDistributor</code> contract:
      </p>
      <ul>
        <li>
          A lightweight keeper service pokes <code>distribute()</code> on a 24-hour schedule. It can't
          change the split, skip a recipient, or pay itself — those rules are enforced by the contract.
        </li>
        <li>
          Each distribution atomically sweeps accumulated BUSDC fees from the Vault, splits them{" "}
          <strong>65% MLP / 25% MONEY stakers / 10% treasury</strong>, deposits the MLP and staker shares
          into their respective reward distributors, and recalibrates the per-second emission rate so the
          new balance pays out smoothly over the next 7-day epoch.
        </li>
        <li>
          MLP holders see fees automatically appreciating into MLP price. MONEY / esMONEY stakers can
          claim their accumulated share at any time on the <Link to="/earn">Earn</Link> page.
        </li>
      </ul>
      <p>
        Verify the contracts live on the BESC explorer:{" "}
        <a href="https://explorer.beschyperchain.com/address/0x3FFC52BE6ff10086119815434BA35B917b7e49AF#code"
           target="_blank" rel="noreferrer">main-vault distributor</a>{" "}/{" "}
        <a href="https://explorer.beschyperchain.com/address/0x75527D2FDd61Ce95deF119f724E1a2bBE708628d#code"
           target="_blank" rel="noreferrer">synth-vault distributor</a>.
      </p>

      <h2>Risks of Holding MLP</h2>
      <ul>
        <li>
          <strong>Directional risk</strong>: the pool holds WBESC, MONEY, and BUSDC. If WBESC or MONEY
          fall in USD price, your MLP value in USD terms falls correspondingly. BUSDC is a stable.
        </li>
        <li>
          <strong>Trader counterparty risk</strong>: if traders are collectively profitable over a period,
          the pool pays out more than it receives in fees, temporarily depressing MLP price. (Adaptive funding
          described above dramatically reduces the long-term severity of this vs v1, but does not eliminate
          short-term variance.)
        </li>
        <li>
          <strong>Smart contract risk</strong>: all DeFi protocols carry smart contract risk despite audits.
        </li>
      </ul>
      <p>
        Historically across major perpetual DEXs, fee income has consistently exceeded trader net profits,
        resulting in positive long-term returns for LP holders — and with v2's per-side funding, those returns
        compound faster during periods of one-sided market sentiment.
      </p>

      <h2>Staking MLP for esMONEY</h2>
      <p>
        MLP is automatically staked when you buy it. You earn both:
      </p>
      <ul>
        <li><strong>BUSDC</strong> — from the 65% fee share (distributed continuously)</li>
        <li><strong>esMONEY</strong> — additional governance token rewards that vest into MONEY</li>
      </ul>
    </>
  );
}

// ─── Section: Staking ─────────────────────────────────────────────────────────

function SStaking() {
  return (
    <>
      <h1>Staking MONEY</h1>
      <p className="Docs-lead">
        Stake your MONEY tokens in the Earn page to earn BUSDC fee rewards, esMONEY, and Multiplier Points
        that boost your yield over time.
      </p>

      <h2>What You Earn by Staking</h2>
      <DataTable
        headers={["Reward", "Description", "Notes"]}
        rows={[
          ["BUSDC", "25% of all platform trading fees", "Claimed manually on the Earn page"],
          ["esMONEY", "Escrowed MONEY rewards", "Can be staked for more yield, or vested over 1 year → MONEY"],
          ["Multiplier Points", "Boost your APR over time", "Lost proportionally if you unstake MONEY"],
        ]}
      />

      <h2>How to Stake MONEY</h2>
      <Step n={1}>Go to <Link to="/earn">Earn</Link>.</Step>
      <Step n={2}>Under the <strong>MONEY</strong> card, click <strong>Stake</strong>.</Step>
      <Step n={3}>Enter the amount of MONEY to stake.</Step>
      <Step n={4}>Confirm the transaction. You start earning rewards immediately.</Step>

      <h2>Multiplier Points (MP)</h2>
      <p>
        Multiplier Points are earned at a rate of <strong>100% APR</strong> on your staked MONEY. They boost
        the BUSDC fee APR component of your rewards.
      </p>
      <p>
        For example: if you stake 1,000 MONEY for 1 year, you'll have accumulated ~1,000 MP. These MPs
        boost your fee rewards proportionally to how much MONEY+esMONEY you have staked vs total supply.
      </p>
      <Callout type="warn">
        If you unstake MONEY, a proportional amount of Multiplier Points are <strong>burned</strong>.
        For example, unstaking 50% of your MONEY burns 50% of your MPs. Plan your staking strategy
        with this in mind — unstaking early destroys your accumulated yield boost.
      </Callout>

      <h2>Staking esMONEY</h2>
      <p>
        esMONEY earned as rewards can be staked directly in the Earn page. Staked esMONEY earns the exact same
        rewards as staked MONEY: BUSDC fees + esMONEY + Multiplier Points.
      </p>
      <p>
        This allows compound growth — your esMONEY rewards generate more esMONEY, which you can stake for
        more rewards, or eventually vest into real MONEY.
      </p>

      <h2>Claiming Rewards</h2>
      <p>
        Rewards accumulate continuously. You can claim at any time:
      </p>
      <ul>
        <li>
          <strong>Claim BUSDC</strong> — click Claim in the MONEY staking card or use Compound
          (which reinvests rewards automatically)
        </li>
        <li>
          <strong>Compound</strong> — stakes your esMONEY and Multiplier Point rewards automatically,
          maximising long-term APR without manual transactions
        </li>
      </ul>

      <h2>Unstaking</h2>
      <Step n={1}>Go to <Link to="/earn">Earn</Link>.</Step>
      <Step n={2}>Click <strong>Unstake</strong> under the MONEY card.</Step>
      <Step n={3}>Enter the amount to unstake. Review the MP burn warning.</Step>
      <Step n={4}>Confirm. MONEY is returned to your wallet immediately.</Step>
      <Callout type="warn">
        Unstaking esMONEY that is reserved for vesting will cancel the vesting process.
        See the <strong>Vesting</strong> section for details.
      </Callout>
    </>
  );
}

// ─── Section: Vesting ─────────────────────────────────────────────────────────

function SVesting() {
  return (
    <>
      <h1>Vesting — esMONEY → MONEY</h1>
      <p className="Docs-lead">
        esMONEY is an escrowed version of MONEY earned through staking. It can be converted into real,
        freely transferable MONEY by depositing it into the Vester contract and waiting 1 year.
      </p>

      <h2>What is esMONEY?</h2>
      <p>
        esMONEY (Escrowed MONEY) is a non-transferable rewards token. You earn it by:
      </p>
      <ul>
        <li>Staking MONEY in the Earn page</li>
        <li>Holding MLP (which is auto-staked)</li>
      </ul>
      <p>
        esMONEY can be staked just like MONEY to earn more rewards. It cannot be sold or transferred until
        it has been through the full vesting process.
      </p>

      <h2>How Vesting Works</h2>
      <p>
        Deposit esMONEY into the Vester contract. It converts linearly into MONEY over <strong>365 days</strong>.
        You can claim the vested MONEY at any time as it becomes available.
      </p>
      <CodeBlock>{`Example:
  Deposit:     100 esMONEY on Day 0
  Day 30:      ~8.2 MONEY claimable (30/365 of 100)
  Day 180:     ~49.3 MONEY claimable
  Day 365:     100 MONEY fully claimable`}</CodeBlock>

      <h2>The Vesting Reserve Requirement</h2>
      <p>
        To vest esMONEY, you must <strong>reserve</strong> an equivalent amount of MONEY or MLP tokens as
        collateral. The reserve ratio is based on how much average MONEY/MLP you held while earning that esMONEY.
      </p>
      <p>
        For example, if you staked 1,000 MONEY for 6 months and earned 50 esMONEY:
      </p>
      <CodeBlock>{`Reserve required ≈ (50 / max_vestable_from_1000_MONEY) × your staked MONEY
→ roughly 1,000 MONEY must be reserved during the vest`}</CodeBlock>
      <Callout type="info">
        Reserved tokens are still staked and still earn rewards during the vesting period.
        You do not lose any yield while vesting.
      </Callout>
      <Callout type="warn">
        If you unstake or withdraw the reserved MONEY/MLP, vesting is <strong>paused</strong>. Already
        vested MONEY is claimable, but unvested esMONEY is returned to your balance. Re-depositing
        resumes vesting from where it left off.
      </Callout>

      <h2>Two Vestors</h2>
      <p>There are two separate vesting contracts:</p>
      <DefTable rows={[
        ["MONEY Vester", "For esMONEY earned from staking MONEY. Reserve with staked MONEY."],
        ["MLP Vester",   "For esMONEY earned from holding MLP. Reserve with your MLP balance."],
      ]} />

      <h2>How to Start Vesting</h2>
      <Step n={1}>Go to <Link to="/earn">Earn</Link> → scroll to the Vest section.</Step>
      <Step n={2}>Click <strong>Deposit</strong> on either the MONEY Vester or MLP Vester card.</Step>
      <Step n={3}>Enter the amount of esMONEY to deposit.</Step>
      <Step n={4}>Review the reserve requirement (MONEY or MLP that will be locked).</Step>
      <Step n={5}>Confirm the deposit transaction.</Step>
      <Step n={6}>Check back any time to claim the vested MONEY that has accumulated.</Step>

      <h2>Claiming Vested MONEY</h2>
      <p>
        Go to Earn → Total Rewards section → click <strong>Claim</strong>. This claims all available
        vested MONEY from both vestors at once.
      </p>

      <h2>Withdrawing from the Vester</h2>
      <p>
        You can withdraw at any time. Already-vested MONEY is claimable. Unvested esMONEY is returned to
        your wallet. Reserved MONEY/MLP is unlocked.
      </p>
    </>
  );
}

// ─── Section: Copy Trading ────────────────────────────────────────────────────

function SCopyTrading() {
  return (
    <>
      <h1>Copy Trading</h1>
      <p className="Docs-lead">
        Automatically mirror the positions of top traders on MoneyX. When a trader you follow opens or
        closes a position, the same trade executes in your account proportionally.
      </p>

      <h2>How It Works</h2>
      <p>
        Copy trading on MoneyX is fully on-chain. When you follow a trader, you authorise a smart contract
        to place trades on your behalf, mirroring the leader's exact position direction and asset using your
        chosen allocation settings.
      </p>

      <h2>Finding Traders to Follow</h2>
      <Step n={1}>Go to <Link to="/copytrading">Copy Trading</Link>.</Step>
      <Step n={2}>Browse the leaderboard. Filter by Top Traders, Most Copied, or Rising.</Step>
      <Step n={3}>
        Review each trader's stats:
        <ul>
          <li><strong>Win Rate</strong> — percentage of profitable trades</li>
          <li><strong>Total PnL</strong> — overall profit/loss in USD</li>
          <li><strong>Followers</strong> — number of people currently copying them</li>
          <li><strong>30D Volume</strong> — trading activity over the last 30 days</li>
        </ul>
      </Step>
      <Step n={4}>Click on a trader to see their full profile and open position history.</Step>

      <h2>Following a Trader</h2>
      <Step n={1}>Click <strong>Follow</strong> on the trader's card or profile page.</Step>
      <Step n={2}>
        Set your allocation parameters:
        <ul>
          <li><strong>Collateral per trade</strong> — how much BUSDC to allocate to each copied trade</li>
          <li><strong>Max total allocation</strong> — maximum total BUSDC deployed across all copied positions</li>
        </ul>
      </Step>
      <Step n={3}>
        Confirm the follow transaction. The smart contract is now authorised to trade on your behalf.
      </Step>

      <Callout type="info">
        Your funds stay in your wallet at all times. The copy trading contract only has permission to
        open/close positions — it cannot withdraw your tokens.
      </Callout>

      <h2>Managing Your Copies</h2>
      <p>Go to <Link to="/copytrading/portfolio">My Portfolio</Link> to see:</p>
      <ul>
        <li>All positions currently being copied</li>
        <li>The trader leader for each position</li>
        <li>Your P&L on each copied trade</li>
        <li>Buttons to close individual positions or unfollow traders</li>
      </ul>

      <h2>Unfollowing a Trader</h2>
      <p>
        Click <strong>Unfollow</strong> on the trader's profile or in your My Following tab. Any positions
        that were copied remain open in your account — they are not automatically closed when you unfollow.
        You can close them manually from the Positions tab on the Trade page.
      </p>

      <h2>Becoming a Trader (Signal Provider)</h2>
      <p>
        Any trader can register to appear on the leaderboard and accept followers. On the Copy Trading page,
        click <strong>Register as Trader</strong> and confirm the transaction. Your address will appear on
        the leaderboard and other users can start copying you.
      </p>
      <Callout type="tip">
        As a followed trader, your followers copy your strategy automatically. There is no fee to followers
        on the current version of copy trading.
      </Callout>

      <h2>Risks of Copy Trading</h2>
      <ul>
        <li>Past performance does not guarantee future results — even top traders have losing streaks</li>
        <li>Copied positions use real collateral from your wallet</li>
        <li>Sudden market moves can liquidate copied positions before you can react</li>
        <li>Set a conservative <em>max total allocation</em> to limit your total exposure</li>
      </ul>
    </>
  );
}

// ─── Section: Referrals ───────────────────────────────────────────────────────

function SReferrals() {
  return (
    <>
      <h1>Referral Program</h1>
      <p className="Docs-lead">
        Earn a percentage of trading fees from every trader you refer to MoneyX — on-chain, automatically,
        forever, with no minimum payout.
      </p>

      <h2>Tier System</h2>
      <DataTable
        headers={["Tier", "Requirement", "Trader Discount", "Your Commission"]}
        rows={[
          ["Tier 1", "Create any code", "5% fee rebate",  "5% of trader's fees"],
          ["Tier 2", "15+ active referrals & $5k+ volume", "10% fee rebate", "10% of trader's fees"],
          ["Tier 3", "30+ active referrals & $25k+ volume", "10% fee rebate", "15% of trader's fees"],
        ]}
      />
      <Callout type="tip">
        Both you and the trader benefit: traders pay less in fees, you earn more over time.
        There is no cap on the number of traders you can refer.
      </Callout>

      <h2>Creating a Referral Code</h2>
      <Step n={1}>Go to <Link to="/referrals">Referrals</Link>.</Step>
      <Step n={2}>Click the <strong>Affiliates</strong> tab.</Step>
      <Step n={3}>Click <strong>Create Code</strong>. Enter a unique alphanumeric code (max 20 chars).</Step>
      <Step n={4}>Confirm the transaction. Your code is registered on-chain — permanent and immutable.</Step>
      <Step n={5}>Share your code or your referral link: <code>https://moneyxpro.com/#/?ref=YOURCODE</code></Step>

      <h2>Using a Referral Code (as a Trader)</h2>
      <Step n={1}>Go to <Link to="/referrals">Referrals</Link> → <strong>Traders</strong> tab.</Step>
      <Step n={2}>Click <strong>Enter Code</strong>.</Step>
      <Step n={3}>Type the affiliate's code and confirm the transaction.</Step>
      <Step n={4}>Your discount is applied to all trades immediately.</Step>

      <Callout type="info">
        A referral code is tied to your address on-chain. You can only be registered under one code at a time.
        To switch codes, simply re-register with a different code.
      </Callout>

      <h2>Claiming Commission</h2>
      <p>
        Commissions accumulate in BUSDC as the referred traders pay fees. Claim them at any time on the
        Referrals page → Affiliates tab → <strong>Claim</strong>.
      </p>

      <h2>Affiliate Link Format</h2>
      <CodeBlock>{`https://moneyxpro.com/#/?ref=YOURCODE

Or append to any page:
https://moneyxpro.com/#/v1?ref=YOURCODE`}</CodeBlock>
      <p>
        When a user visits your link, the referral code is stored in their browser. If they connect their
        wallet on the same session, the code is automatically registered for them.
      </p>
    </>
  );
}

// ─── Section: Speed Up ────────────────────────────────────────────────────────

function SSpeedUp() {
  return (
    <>
      <h1>Speed Up Page Loading</h1>
      <p className="Docs-lead">
        MoneyX fetches on-chain data directly from BESC Hyperchain. If things feel slow, here's how to fix it.
      </p>

      <h2>Set a Custom RPC</h2>
      <p>
        The default public RPC can be congested during high-traffic periods. Setting your own node or using
        a dedicated RPC service dramatically improves speed.
      </p>
      <Step n={1}>Click the <strong>Settings ⚙️</strong> icon in the top-right of the app.</Step>
      <Step n={2}>Find the <strong>RPC URL</strong> field.</Step>
      <Step n={3}>Enter a fast BESC Hyperchain RPC endpoint.</Step>
      <Step n={4}>Save. The page will reload using the new RPC.</Step>
      <CodeBlock>{`Default RPC:  https://rpc.beschyperchain.com`}</CodeBlock>

      <h2>Browser Optimisations</h2>
      <ul>
        <li><strong>Use Chrome or Brave</strong> — best compatibility with Web3 wallets and Web3 RPCs</li>
        <li><strong>Disable ad-blockers</strong> during trading — some filters block RPC WebSocket connections</li>
        <li><strong>Hard reload</strong> if data looks stale: <code>Ctrl+Shift+R</code> (Windows) / <code>Cmd+Shift+R</code> (Mac)</li>
        <li><strong>Clear browser cache</strong> if the page fails to load: Settings → Privacy → Clear data</li>
        <li><strong>Close extra tabs</strong> — each tab with MoneyX open makes its own RPC polling requests</li>
      </ul>

      <h2>MetaMask Speed Tips</h2>
      <ul>
        <li>Update MetaMask to the latest version</li>
        <li>
          In MetaMask → Networks → BESC Hyperchain, set the RPC to <code>https://rpc.beschyperchain.com</code>
        </li>
        <li>If MetaMask is slow to sign transactions, try switching to a hardware wallet or WalletConnect</li>
      </ul>

      <h2>Mobile Performance</h2>
      <ul>
        <li>Use <strong>MetaMask Mobile</strong> or <strong>Trust Wallet</strong> with built-in browser</li>
        <li>Ensure a stable WiFi connection — mobile data can have inconsistent latency with RPC nodes</li>
        <li>Close background apps to free RAM for the browser</li>
      </ul>

      <h2>Transactions Not Appearing?</h2>
      <p>If your transaction was confirmed on-chain but not showing in the app:</p>
      <ol>
        <li>Wait 10–15 seconds — keeper execution adds a short delay</li>
        <li>Hard-reload the page</li>
        <li>Check the transaction on <ExternalLink href="https://explorer.beschyperchain.com">BESC Explorer</ExternalLink></li>
        <li>If it shows as "Pending" in MetaMask, try speeding up the transaction with a higher gas price</li>
      </ol>

      <Callout type="tip">
        If you're experiencing consistent issues, try a different browser or incognito mode to rule out
        extension conflicts.
      </Callout>
    </>
  );
}

// ─── Section: FAQ ─────────────────────────────────────────────────────────────

function SFAQ() {
  return (
    <>
      <h1>Frequently Asked Questions</h1>

      <h3 className="Docs-faq-category">Trading</h3>

      <FAQItem q="What is the maximum leverage?">
        The current maximum is <strong>20×</strong>. The protocol is architected for up to <strong>100×</strong>,
        and the cap is raised in steps as the MLP liquidity pool grows — keeping leverage in line with the
        liquidity that backs it. Per-market open-interest caps and the per-side adaptive funding rate keep
        individual market risk bounded — see the <Link to="/docs/liquidity">Liquidity</Link> section.
      </FAQItem>

      <FAQItem q="What assets can I trade?">
        30+ markets. Native pool tokens: <strong>WBESC, MONEY</strong>. Synthetic crypto:{" "}
        <strong>BTC, ETH, BNB, SOL, XRP, DOGE, ADA, TRX, LINK, AVAX, SHIB, TON, DOT, NEAR, MATIC, LTC, UNI,
        ATOM, ARB, OP</strong>. Synthetic stocks (NYSE / NASDAQ hours only):{" "}
        <strong>AAPL, TSLA, NVDA, MSFT, GOOGL, AMZN, META, SPY, QQQ, GOLD</strong>. All positions are USD-denominated.
      </FAQItem>

      <FAQItem q="Is there a minimum trade size?">
        The minimum position size is approximately <strong>$10 USD equivalent</strong>. Very small positions
        are not economical after fees.
      </FAQItem>

      <FAQItem q="What can I use as collateral?">
        Only the three pool tokens — <strong>WBESC, MONEY, BUSDC</strong>. Synthetic markets (BTC, ETH, stocks, etc.)
        are USD-denominated indices and don't have real on-chain tokens to post as collateral; opens / closes on those
        markets are settled in BUSDC.
      </FAQItem>

      <FAQItem q="Why was my order cancelled?">
        Market orders are cancelled if the oracle price moves more than your allowed slippage before the keeper
        executes. Increase your slippage tolerance in Settings, or use a limit order instead.
      </FAQItem>

      <FAQItem q="What is the difference between mark price and index price?">
        The <strong>mark price</strong> (oracle price) is a median aggregate from multiple price sources, used
        to calculate P&L and trigger liquidations. It is more robust than a single exchange's last price.
        The "index price" in some contexts refers to a specific exchange's price — MoneyX uses oracle price only.
      </FAQItem>

      <h3 className="Docs-faq-category">Fees & Costs</h3>

      <FAQItem q="What does it cost to trade?">
        0.1% open fee + 0.1% close fee = 0.2% round trip on position size. Plus borrow fee (hourly, varies
        by utilisation). Plus a small BESC execution fee for the keeper.
      </FAQItem>

      <FAQItem q="Do I pay fees on unrealised PnL?">
        No. Fees are on position size (for open/close) and accrued on position size (for borrow). P&L only
        realises when you close.
      </FAQItem>

      <FAQItem q="How do I reduce my trading fees?">
        Use a referral code for a 5–10% fee rebate. Reduce the duration positions are held to minimise
        borrow fees. Refer others to earn commissions that offset your own fees.
      </FAQItem>

      <h3 className="Docs-faq-category">Liquidation & Risk</h3>

      <FAQItem q="What happens if I get liquidated?">
        Your position is closed automatically. Any remaining collateral after the liquidation fee is returned
        to your wallet. You cannot lose more than your deposited collateral — there is no debt.
      </FAQItem>

      <FAQItem q="Can I lose more than I deposit?">
        No. MoneyX does not support margin debt. Your maximum loss is your deposited collateral.
      </FAQItem>

      <FAQItem q="Is there a stop-loss guarantee?">
        Stop-loss orders use the oracle price and are executed by keeper bots. In extreme market conditions
        (oracle gaps, network congestion), the fill price may be slightly different from your exact SL price.
        Set your SL well above the liquidation price to avoid gap-down liquidation.
      </FAQItem>

      <h3 className="Docs-faq-category">MLP & Staking</h3>

      <FAQItem q="How much can I earn as an MLP holder?">
        APR varies with trading volume. All 65% of platform fees go to MLP holders. On high-volume days,
        this can represent a significant annualised yield. Check the current APR on the Earn page.
      </FAQItem>

      <FAQItem q="How often are staking rewards distributed?">
        Rewards accumulate continuously per block and can be claimed at any time. There is no fixed
        distribution schedule.
      </FAQItem>

      <FAQItem q="What is esMONEY and why can't I sell it?">
        esMONEY is an escrowed (locked) form of MONEY used as a staking reward. It cannot be transferred
        until it has been vested (converted to MONEY over 1 year). This prevents immediate sell pressure
        and rewards long-term participants.
      </FAQItem>

      <FAQItem q="I unstaked MONEY and my Multiplier Points dropped — why?">
        Multiplier Points are tied to your staked MONEY. Unstaking burns MPs proportionally to prevent
        users from accumulating MPs and then dumping their stake. MPs are a reward for long-term staking —
        the longer you stay, the more your yield is boosted.
      </FAQItem>

      <h3 className="Docs-faq-category">Platform & Security</h3>

      <FAQItem q="Is MoneyX audited?">
        MoneyX is built on top of the GMX V1 architecture — a battle-tested perpetuals codebase with{" "}
        years of mainnet operation across multiple chains and over $1B in historical TVL. We used it as
        the foundation, then extended it with the per-side adaptive funding fix (which addresses the v1
        MLP-bleed), the on-chain <code>AutoFeeDistributor</code> (eliminating manual weekly admin runs),
        and per-side OI caps configured per market. All new and modified contracts are verified on the
        BESC explorer — see the <Link to="/docs/contracts">Contracts</Link> section. Trade responsibly
        and never risk more than you can afford to lose.
      </FAQItem>

      <FAQItem q="Can I use MoneyX on mobile?">
        Yes. The interface is fully responsive. Use MetaMask Mobile (built-in browser), Trust Wallet, or
        any WalletConnect-compatible mobile wallet for the best experience.
      </FAQItem>

      <FAQItem q="How do I add BESC Hyperchain to MetaMask?">
        <div>
          Go to MetaMask → Networks → Add Network → Add Manually:
          <br /><br />
          <CodeBlock>{`Name:     BESC Hyperchain
RPC:      https://rpc.beschyperchain.com
Chain ID: 2372
Symbol:   BESC
Explorer: https://explorer.beschyperchain.com`}</CodeBlock>
        </div>
      </FAQItem>

      <FAQItem q="How do I contact support?">
        For technical issues or questions, email{" "}
        <ExternalLink href="mailto:support@besc.services">support@besc.services</ExternalLink>. Community
        support is available in the MoneyX Telegram and Discord.
      </FAQItem>
    </>
  );
}

// ─── Section: BESC Network ────────────────────────────────────────────────────

function SBescNetwork() {
  return (
    <>
      <h1>BESC Hyperchain Network</h1>
      <p className="Docs-lead">
        MoneyX runs natively on <strong>BESC Hyperchain</strong> — a high-throughput EVM-compatible Layer 1
        blockchain with sub-second finality and near-zero gas costs.
      </p>

      <h2>Network Parameters</h2>
      <CodeBlock>{`Network Name:  BESC Hyperchain
RPC URL:       https://rpc.beschyperchain.com
Chain ID:      2372
Symbol:        BESC
Block Explorer: https://explorer.beschyperchain.com`}</CodeBlock>

      <h2>Adding BESC to MetaMask</h2>
      <Step n={1}>Open MetaMask → click the network dropdown at the top.</Step>
      <Step n={2}>Click <strong>Add Network</strong> → <strong>Add a network manually</strong>.</Step>
      <Step n={3}>Fill in the network parameters above and click Save.</Step>
      <Step n={4}>Switch to BESC Hyperchain. Your BESC balance will appear.</Step>
      <Callout type="tip">
        You can also visit <Link to="/buy">Buy</Link> and click "Add BESC Hyperchain" — MoneyX will
        prompt MetaMask to add the network automatically.
      </Callout>

      <h2>Gas on BESC</h2>
      <p>
        BESC Hyperchain has a <strong>fixed minimum gas price of 1,000 Gwei</strong>. All transactions
        pay this base rate — there is no gas auction or fee spikes. A full position open costs less than
        $0.01 in gas.
      </p>
      <DataTable
        headers={["Action", "Approx. Gas Units", "Cost at 1000 Gwei"]}
        rows={[
          ["Open position",       "~350,000", "< $0.01"],
          ["Close position",      "~280,000", "< $0.01"],
          ["Add/Remove collateral","~200,000", "< $0.01"],
          ["Claim rewards",       "~150,000", "< $0.01"],
          ["Buy/Sell MLP",        "~250,000", "< $0.01"],
        ]}
      />
      <Callout type="info">
        BESC gas costs are negligible compared to Ethereum mainnet. You only need a small amount of BESC
        in your wallet to cover many hundreds of transactions.
      </Callout>

      <h2>Getting BESC for Gas</h2>
      <p>The easiest ways to get BESC for gas fees:</p>
      <ul>
        <li>
          <strong>Faucet</strong> — visit the BESC faucet at <code>faucet.beschyperchain.com</code> for a
          small amount of BESC to cover your first transactions.
        </li>
        <li>
          <strong>Buy on MoneyX</strong> — go to <Link to="/buy">Buy</Link> to acquire BESC with a card or
          crypto from another chain.
        </li>
        <li>
          <strong>Bridge</strong> — bridge BESC from Ethereum or BSC using a compatible bridge.
        </li>
      </ul>

      <h2>Getting BUSDC (Trading Collateral)</h2>
      <p>
        BUSDC is the primary collateral token on MoneyX. It is a <strong>bridged USDC</strong> on BESC
        Hyperchain. The contract address is{" "}
        <code>0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B</code>.
      </p>
      <ul>
        <li>
          <strong>Swap</strong> — on the <Link to="/buy">Buy page</Link>, swap BESC or other tokens for BUSDC.
        </li>
        <li>
          <strong>Bridge</strong> — bridge USDC from Ethereum/BSC to BESC Hyperchain.
        </li>
        <li>
          <strong>Earn</strong> — buy MLP or stake MONEY to earn BUSDC fee rewards over time.
        </li>
      </ul>

      <h2>Block Explorer</h2>
      <p>
        Verify any transaction at{" "}
        <ExternalLink href="https://explorer.beschyperchain.com">explorer.beschyperchain.com</ExternalLink>.
        Every trade, position, fee claim, and contract interaction is fully on-chain and publicly auditable.
      </p>

      <h2>Why BESC Hyperchain?</h2>
      <DefTable rows={[
        ["Speed", "Sub-second block times — positions execute in under 15 seconds end-to-end."],
        ["Cost", "Fixed 1000 Gwei gas with negligible cost per trade — no Ethereum gas spikes."],
        ["EVM Compatible", "Works with MetaMask, WalletConnect, Hardhat, Ethers.js, Viem — no new tooling needed."],
        ["Finality", "Fast finality — no waiting for multiple confirmations before your position is live."],
      ]} />
    </>
  );
}

// ─── Section: Fee Distribution ────────────────────────────────────────────────

function SFeeDistribution() {
  return (
    <>
      <h1>Fee Distribution — Where Every Fee Goes</h1>
      <p className="Docs-lead">
        Every fee paid on MoneyX is distributed to token holders and liquidity providers automatically,
        in real time, with no intermediary.
      </p>

      <h2>The Two Buckets</h2>
      <DataTable
        headers={["Recipient", "Share", "How Distributed"]}
        rows={[
          ["MLP Liquidity Providers", "65%", "Reflected in MLP price — automatic, no claim needed"],
          ["MONEY / esMONEY Stakers", "25%", "Accumulated in reward tracker — claim manually on Earn page"],
          ["Admin / Treasury",         "10%", "Sent directly to admin wallet on each distribution"],
        ]}
      />

      <h2>Fee Types and Sources</h2>
      <DataTable
        headers={["Fee Type", "Rate", "Basis"]}
        rows={[
          ["Open fee",            "0.1%",           "Position size (marginFeeBasisPoints = 10)"],
          ["Close fee",           "0.1%",           "Position size"],
          ["Borrow (utilisation)","Up to 0.01% / 8h","Position size × (reserved/pool) × factor"],
          ["Adaptive funding",    "0–0.01% / 8h",   "Position size × OI skew × factor (dominant side only)"],
          ["Swap fee — volatile", "0.25% base ±dyn", "Swap notional (BUSDC↔WBESC/MONEY)"],
          ["Swap fee — stable",   "0.01% base ±dyn", "BUSDC ↔ BUSDC (rare; dynamic always low)"],
          ["Liquidation fee",     "$5 flat",        "Paid from collateral on liquidation"],
        ]}
      />

      <h2>How MLP Holders Receive Fees</h2>
      <p>
        When fees are generated, the fee amount in BUSDC is added to the MLP pool's assets. Since MLP
        price = (total pool value) / (MLP supply), fees increase the pool value, which raises the MLP price.
        You do not need to claim — your MLP automatically appreciates in value as fees accumulate.
      </p>
      <Callout type="tip">
        Sell MLP at any time to realise your accumulated fee income. The difference between your buy price
        and sell price reflects all fees earned during your holding period, plus or minus trader P&L.
      </Callout>

      <h2>How Stakers Receive Fees</h2>
      <p>
        25% of all fees are routed to the <strong>Fee Reward Tracker</strong> contracts
        (<code>FeeMoneyTracker</code> and <code>FeeMlpTracker</code>). These contracts track each staker's
        share in real time. The amount claimable grows every block. You can claim at any time from the
        <Link to="/earn"> Earn page</Link>.
      </p>
      <p>
        Fee rewards are distributed in <strong>BUSDC</strong> (not MONEY or esMONEY). This means stakers
        get a real stablecoin income stream regardless of MONEY token price fluctuations.
      </p>

      <h2>Referral Fee Flow</h2>
      <p>
        When a trader uses a referral code, the fee they pay is rebated. Part of the rebate goes back to the
        trader as a discount, part goes to the affiliate as commission. Tiers are configured on-chain in
        <code>ReferralStorage</code>; the trader's effective rate depends on their affiliate's tier.
      </p>
      <DataTable
        headers={["Tier", "Total Rebate", "Trader Discount Share", "Affiliate Share", "Effective Trader Discount", "Affiliate Commission"]}
        rows={[
          ["Tier 0", "10%", "50%", "50%", "5% of fees back", "5% of fees"],
          ["Tier 1", "20%", "60%", "40%", "12% of fees back", "8% of fees"],
          ["Tier 2", "27%", "44.44%", "55.56%", "12% of fees back", "15% of fees"],
        ]}
      />
      <CodeBlock>{`Example — Tier 1 affiliate, trader opens a $10,000 position:
  Base open fee:        0.1% × $10,000 = $10.00
  Total rebate (20%):   $10.00 × 20%   = $2.00
    → Trader discount (60% of rebate): $1.20  (effective fee: $8.80)
    → Affiliate commission (40%):      $0.80
  Net protocol fee:     $10.00 − $2.00 = $8.00
  → That $8.00 is then split 65 / 25 / 10 to MLP / Stakers / Treasury`}</CodeBlock>

      <h2>Copy Trading Fee Flow</h2>
      <p>
        When a copy trading leader opens a position, the follower's position opens with the same direction
        and asset. All fees apply to the follower's position exactly as if they had placed the trade manually.
        There is no additional copy trading fee on the current platform version.
      </p>

      <h2>Borrow Fee Accumulation Rate</h2>
      <p>
        Borrow (utilisation) fees accumulate once per 8-hour funding interval, proportional to how much of
        the pool is currently borrowed by open positions. Combined with the per-side adaptive funding fee,
        they're deducted from your collateral when you close.
      </p>
      <CodeBlock>{`Borrow fee per 8h interval = position_size × (reserved / pool) × 100 / 1,000,000

Example at 50% pool utilisation:
  position_size = $50,000
  rate per 8h  = 50% × 100/1,000,000 = 0.005%
  cost per 8h  = $2.50
  cost per day = $7.50  (≈ 5.5% APR at constant utilisation)

Compare: at 100% utilisation the maximum is ~10.95% APR — significantly lower
than v1's effective rates because v2 also charges adaptive funding on the
dominant OI side, which absorbs the bulk of skew-related compensation.`}</CodeBlock>

      <h2>Contract-Level Flow (Fully Automated)</h2>
      <p>
        Fee collection and distribution are fully on-chain via the <code>AutoFeeDistributor</code> contract.
        No manual weekly admin runs — a lightweight keeper just pokes <code>distribute()</code> on a 24-hour
        schedule and the split is enforced by the contract itself:
      </p>
      <ol>
        <li>Trader calls <strong>Router.increasePosition()</strong> → Vault accumulates the open fee in <code>feeReserves[BUSDC]</code>.</li>
        <li>Keeper calls <strong>AutoFeeDistributor.distribute()</strong> (≥ once per day, automatically by the PM2 fee-distributor service).</li>
        <li>The contract sweeps <code>Vault.collectFees(BUSDC, self)</code>, then atomically splits:
          <ul>
            <li><strong>65%</strong> → transferred to the <code>FeeMlpDistributor</code> + <code>tokensPerInterval</code> recalibrated so MLP holders earn it smoothly over the 7-day epoch.</li>
            <li><strong>25%</strong> → transferred to the <code>FeeMoneyDistributor</code> + same recalibration for MONEY / esMONEY stakers.</li>
            <li><strong>10%</strong> → sent directly to the treasury wallet.</li>
          </ul>
        </li>
        <li>MLP holders see their share automatically appreciating in the MLP price.</li>
        <li>MONEY / esMONEY stakers claim their share at any time via <strong>RewardRouter.claimFees()</strong>.</li>
      </ol>
      <Callout type="tip">
        The percentages are enforced by the on-chain contract (<code>mlpShareBps=6500</code>,{" "}
        <code>moneyShareBps=2500</code>, <code>adminShareBps=1000</code>) — they can only be changed by a
        governance transaction. No keeper can pay itself, change the split, or skip a recipient. Fully auditable
        on the BESC explorer:{" "}
        <a href="https://explorer.beschyperchain.com/address/0x3FFC52BE6ff10086119815434BA35B917b7e49AF#code" target="_blank" rel="noreferrer">main-vault distributor</a>{" "}/{" "}
        <a href="https://explorer.beschyperchain.com/address/0x75527D2FDd61Ce95deF119f724E1a2bBE708628d#code" target="_blank" rel="noreferrer">synth-vault distributor</a>.
      </Callout>
    </>
  );
}

// ─── Section: Risk Management ─────────────────────────────────────────────────

function SRiskManagement() {
  return (
    <>
      <h1>Risk Management</h1>
      <p className="Docs-lead">
        Leveraged perpetuals are powerful tools — and powerful tools can cause significant losses if
        misused. This section covers professional risk practices for protecting your capital.
      </p>

      <h2>The Golden Rules</h2>
      <div className="Docs-feature-grid">
        {[
          ["🛑", "Always Set a Stop-Loss", "Every position should have a stop-loss. No exceptions. A 20% SL on collateral is a common baseline."],
          ["📏", "Size Positions Small", "Risk no more than 1–2% of your total capital on any single trade. Large positions on high leverage wipe accounts fast."],
          ["📉", "Lower Leverage", "5× to 10× gives a meaningful price buffer. 50×+ is for scalpers who watch the screen constantly."],
          ["⏰", "Watch Borrow Fees", "A profitable position can turn negative from fees alone. Set TP/SL and don't leave leveraged positions open for weeks unmonitored."],
          ["🔀", "Diversify", "Don't put all capital into one directional trade. Spread across assets and time horizons."],
          ["🧠", "Trade with a Plan", "Define entry, exit, and stop before opening. Emotional decisions mid-trade lead to over-leveraging and holding losing positions."],
        ].map(([emoji, title, body]) => (
          <div key={title as string} className="Docs-feature-card">
            <div className="Docs-feature-emoji">{emoji}</div>
            <div className="Docs-feature-title">{title}</div>
            <div className="Docs-feature-body">{body}</div>
          </div>
        ))}
      </div>

      <h2>Leverage Guidelines by Experience</h2>
      <DataTable
        headers={["Trader Type", "Recommended Leverage", "Rationale"]}
        rows={[
          ["Beginner",         "1× – 5×",    "Learn how the platform works without risking large losses"],
          ["Intermediate",     "5× – 20×",   "Meaningful exposure with manageable liquidation buffer"],
          ["Advanced",         "20× – 50×",  "Active risk management required; clear invalidation levels"],
          ["Expert / Scalper", "50× – 100×", "Very tight stop-losses; constant monitoring; defined R:R"],
        ]}
      />
      <Callout type="info">
        These tiers are general guidance for each leverage band. The platform's current maximum is{" "}
        <strong>20×</strong>, so the higher bands unlock as the leverage cap is raised toward 100× alongside
        MLP liquidity growth.
      </Callout>

      <h2>Position Sizing Formula</h2>
      <CodeBlock>{`Max risk per trade: 1–2% of total capital

Example ($10,000 account, 1% risk, 10× leverage):
  Max loss allowed   = $10,000 × 1% = $100
  Stop-loss distance = 10% (below entry on a long)
  Collateral at risk = $100 / 10% = $1,000
  Position size      = $1,000 × 10 leverage = $10,000

  Even if the full $1,000 collateral is lost (stop hits),
  your account only drops 1% ($100).`}</CodeBlock>

      <h2>The Liquidation Buffer Rule</h2>
      <p>
        Your stop-loss should always be set <strong>at least 20–30% above your liquidation price</strong>.
        This buffer prevents a wick through your SL from causing liquidation before the order executes.
      </p>
      <CodeBlock>{`Example (Long BTC, 10× leverage):
  Entry:        $50,000
  Liq. price:   ~$45,450 (approx. 9% below entry)
  Good SL:      $47,500  (5% below entry, ~44% above liq.)
  Too tight SL: $45,600  (near liq. — a wick can liquidate you)`}</CodeBlock>

      <h2>Managing Multiple Positions</h2>
      <ul>
        <li>
          <strong>Correlation risk</strong>: long BTC + long ETH are highly correlated. In a crash,
          both lose simultaneously. Treat them as one directional bet, not two independent positions.
        </li>
        <li>
          <strong>Total exposure</strong>: sum all position sizes × leverage. If total notional exposure
          is 5× your account, you have effectively 5× leverage across the board.
        </li>
        <li>
          <strong>Check the Positions tab daily</strong> — borrow fees erode collateral and push your
          liquidation price closer over time, even with no price movement.
        </li>
      </ul>

      <h2>Synthetic Markets: Additional Considerations</h2>
      <p>
        Synthetic markets (AAPL, TSLA, NVDA, and most crypto assets on BESC) are priced from oracles.
        During weekends and market holidays, stock synthetics may have wider spreads or limited oracle
        updates. Exercise additional caution when trading stock synthetics outside market hours.
      </p>

      <h2>Copy Trading Risk</h2>
      <ul>
        <li>Past performance of a copied trader does not guarantee future results</li>
        <li>Set a conservative max allocation — your exposure is real even though you're copying</li>
        <li>The copied trader may use very high leverage that you're not comfortable with</li>
        <li>A trader's excellent 30-day stats might not persist — leaders can have large drawdowns</li>
      </ul>

      <Callout type="warn">
        Never invest money you cannot afford to lose. Leveraged perpetual trading is high-risk activity.
        Start with small sizes, learn the platform, and scale up only as you develop consistent discipline.
      </Callout>

      <h2>Useful Risk Monitoring Tools</h2>
      <ul>
        <li>
          <strong>Account Dashboard</strong>: <Link to="/accounts">accounts page</Link> shows all open
          positions, orders, and P&L across your address.
        </li>
        <li>
          <strong>API monitoring</strong>: use the <Link to="/api-reference">REST API</Link> or WebSocket
          to build automated alerts when a position nears its liquidation price.
        </li>
        <li>
          <strong>On-chain explorer</strong>: every position change is on-chain at{" "}
          <ExternalLink href="https://explorer.beschyperchain.com">explorer.beschyperchain.com</ExternalLink>.
        </li>
      </ul>
    </>
  );
}

// ─── Section: Synthetics ─────────────────────────────────────────────────────

function SSynthetics() {
  return (
    <>
      <h1>Synthetic Markets</h1>
      <p className="Docs-lead">
        MoneyX offers synthetic perpetual markets on a wide range of assets — including crypto, major
        tech stocks, and indices. These are fully on-chain, oracle-priced, BUSDC-collateralised markets.
      </p>

      <h2>What Are Synthetic Markets?</h2>
      <p>
        A synthetic market does not hold the underlying asset. Instead, it uses <strong>oracle price feeds</strong>
        to track the real-world price of an asset. All P&L is settled in BUSDC. There is no actual BTC,
        Apple stock, or TSLA in any vault — only price exposure.
      </p>
      <DefTable rows={[
        ["Real market (WBESC)", "Wrapped BESC — the native asset; physically held in the MLP vault"],
        ["Real market (MONEY)", "The protocol's utility / governance token; physically held in the MLP vault"],
        ["Real market (BUSDC)", "Bridged USDC stable; held in the vault for collateral and synth settlement"],
        ["Synthetic market", "BTC, ETH, SOL, AAPL, TSLA, etc. — oracle priced, no real asset held; PnL settled in BUSDC"],
      ]} />

      <h2>SynthVault Architecture</h2>
      <p>
        Synthetic markets use a separate <strong>SynthVault</strong> contract instead of the main MLP vault.
        This isolation means:
      </p>
      <ul>
        <li>Synthetic positions are collateralised purely in BUSDC</li>
        <li>The synthetic OI pool is independent from the main MLP pool</li>
        <li>OI caps are set per asset by governance to control maximum exposure</li>
        <li>Price feeds come from the <strong>SynthVaultPriceFeed</strong> contract (oracle aggregator)</li>
      </ul>

      <h2>Available Synthetic Crypto Markets</h2>
      <DataTable
        headers={["Asset", "Symbol", "Address (BESC)"]}
        rows={[
          ["Bitcoin",      "BTC",  "0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c"],
          ["Ethereum",     "ETH",  "0x2170Ed0880ac9A755fd29B2688956BD959F933F8"],
          ["BNB",          "BNB",  "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c"],
          ["Solana",       "SOL",  "0x570A5D26f7765Ecb712C0924E4De545B89fD43dF"],
          ["XRP",          "XRP",  "0x1D2F0da169ceB9fC7B3144628dB156f3F6c60dBE"],
          ["Dogecoin",     "DOGE", "0xbA2aE424d960c26247Dd6c32edC70B295c744C43"],
          ["Cardano",      "ADA",  "0x3EE2200Efb3400fAbB9AacF31297cBdD1d435D47"],
          ["TRON",         "TRX",  "0x85EAC5Ac2F758618dFa09bDbe0cf174e7d574D5B"],
          ["Chainlink",    "LINK", "0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD"],
          ["Avalanche",    "AVAX", "0x1CE0c2827e2eF14D5C4f29a091d735A204794041"],
          ["Shiba Inu",    "SHIB", "0x2859e4544C4bB03966803b044A93563Bd2D0DD4D"],
          ["Toncoin",      "TON",  "0x76A797A59Ba2C17726896976B7B3747BfD1d220f"],
          ["Polkadot",     "DOT",  "0x7083609fCE4d1d8Dc0C979AAb8c869Ea2C873402"],
          ["NEAR Protocol","NEAR", "0x1Fa4a73a3F0133f0025378af00236f3aBDEE5D63"],
          ["Polygon",      "MATIC","0xCC42724C6683B7E57334c4E856f4c9965ED682bD"],
          ["Litecoin",     "LTC",  "0x4338665CBB7B2485A8855A139b75D5e34AB0DB94"],
          ["Uniswap",      "UNI",  "0xBf5140A22578168FD562DCcF235E5D43A02ce9B1"],
          ["Cosmos",       "ATOM", "0x0Eb3a705fc54725037CC9e008bDede697f62F335"],
          ["Arbitrum",     "ARB",  "0xa050FFb3eEb8200eEB7F61ce34FF644420FD3522"],
          ["Optimism",     "OP",   "0x5C1E3a76ABa3c84afbCE7a38e8ef9dEa3F9Adfc8"],
        ]}
      />

      <h2>Synthetic Stock Markets</h2>
      <p>
        MoneyX also supports synthetic perpetuals on major US stocks. These track real-time stock prices
        via oracle feeds. Note that during non-market hours (weekends, US holidays), oracle updates may
        be less frequent.
      </p>
      <DataTable
        headers={["Asset", "Symbol", "Address (BESC)"]}
        rows={[
          ["Apple Inc.",        "AAPL",  "0x53544f434b000000000000000000000000000001"],
          ["Tesla Inc.",        "TSLA",  "0x53544f434b000000000000000000000000000002"],
          ["NVIDIA Corp.",      "NVDA",  "0x53544f434b000000000000000000000000000003"],
          ["Microsoft",         "MSFT",  "0x53544f434b000000000000000000000000000004"],
          ["Alphabet (Google)", "GOOGL", "0x53544f434b000000000000000000000000000005"],
          ["Amazon",            "AMZN",  "0x53544f434b000000000000000000000000000006"],
          ["Meta Platforms",    "META",  "0x53544f434b000000000000000000000000000007"],
          ["S&P 500 ETF",       "SPY",   "0x53544f434b000000000000000000000000000008"],
          ["Nasdaq-100 ETF",    "QQQ",   "0x53544f434b000000000000000000000000000009"],
          ["Gold (XAU)",        "GOLD",  "0x53544f434b00000000000000000000000000000a"],
        ]}
      />
      <Callout type="info">
        Stock synthetics use padded identifiers (not real ERC-20 tokens). They exist only as price
        identifiers in the oracle system — no real stock is ever held or transferred. Stock markets
        are oracle-priced only during NYSE / NASDAQ trading hours (Mon–Fri, 9:30 AM – 4:00 PM ET);
        outside those windows the price stays frozen at the last update.
      </Callout>

      <h2>How Oracle Pricing Works</h2>
      <p>
        The <strong>SynthVaultPriceFeed</strong> contract aggregates prices from multiple off-chain
        sources (Chainlink, Binance, and other data providers). Prices are pushed on-chain regularly
        and used as the mark price for all synthetic positions.
      </p>
      <ul>
        <li>Prices update sub-second via the live WebSocket feed (used for UI display)</li>
        <li>On-chain oracle updates happen on every significant price movement or time interval</li>
        <li>Keeper bots use the on-chain oracle price for liquidation checks</li>
        <li>No user can manipulate the oracle — it is a median of multiple independent feeds</li>
      </ul>

      <h2>OI Caps (Open Interest Limits)</h2>
      <p>
        Each synthetic market has a maximum open interest cap set by governance to control risk. When
        the cap is reached on one side (long or short), no new positions can be opened in that direction
        until existing positions are reduced. The cap is displayed as "Max Long" and "Max Short" in the
        trade box.
      </p>

      <h2>Collateral for Synthetic Markets</h2>
      <p>
        All synthetic market positions — both long and short — are <strong>collateralised in BUSDC
        exclusively</strong>. This differs from the native MLP-vault markets (WBESC, MONEY) which follow
        the classic perp pattern of token-as-collateral for longs.
      </p>

      <h2>Pay-With vs Collateral — How the Token Selector Works</h2>
      <p>
        The "Pay" box in the trading UI lets you deposit any of the four whitelisted real tokens —{" "}
        <strong>BESC, WBESC, MONEY, BUSDC</strong>. That is <em>not necessarily</em> the token that
        backs your position; the protocol converts behind the scenes:
      </p>
      <DefTable rows={[
        ["You pay BUSDC, you long BTC",         "No conversion. BUSDC is the synth-market collateral."],
        ["You pay WBESC, you long BTC",         "WBESC is swapped to BUSDC at vault rates (small swap fee), then BUSDC backs the position."],
        ["You pay BESC (native), you long BTC", "BESC is wrapped to WBESC, then swapped to BUSDC, then opens the BTC position."],
        ["You pay BUSDC, you long WBESC",       "BUSDC is swapped to WBESC; WBESC then backs the long (native-vault collateral rule)."],
        ["You pay BUSDC, you short WBESC",      "No conversion. BUSDC is the short collateral on the main vault as well."],
      ]} />
      <Callout type="tip">
        Pay-with token only affects what's in your wallet beforehand and the swap fee. The market /
        side combination determines the final collateral token. The "Collateral In" line of the trade
        box always shows what is actually held against your position after conversion.
      </Callout>

      <h2>Step-by-Step: Opening a Synthetic Long (e.g., BTC Long)</h2>
      <Step n={1}>Navigate to <Link to="/v1">/v1</Link> on MoneyX (or click <strong>Trade</strong> in the bottom nav).</Step>
      <Step n={2}>Pick the asset from the chart-token selector — e.g., <strong>BTC/USD</strong>,
        <strong> ETH/USD</strong>, <strong>AAPL/USD</strong>, etc.</Step>
      <Step n={3}>Choose <strong>Long</strong> (top-left toggle in the trade box).</Step>
      <Step n={4}>Pick your <strong>Pay</strong> token — BUSDC is the cheapest path for synths (no swap fee).
        BESC / WBESC / MONEY also work but incur a swap into BUSDC.</Step>
      <Step n={5}>Enter the <strong>Pay amount</strong> (collateral you're depositing).</Step>
      <Step n={6}>Set <strong>Leverage</strong> with the slider (1× – 20× at current liquidity; rises toward 100× as the MLP pool grows). The position size auto-adjusts.</Step>
      <Step n={7}>Review the trade summary: entry price, liquidation price, position fee, swap fee (if any),
        and the per-side adaptive funding rate (more on this below).</Step>
      <Step n={8}>Click <strong>Long BTC</strong>. Approve the token (one-time) and confirm the position in
        your wallet. The position appears in the Positions tab within seconds.</Step>

      <h2>Step-by-Step: Opening a Synthetic Short</h2>
      <Step n={1}>Same as above through step 2 (pick the market).</Step>
      <Step n={2}>Choose <strong>Short</strong> instead of Long.</Step>
      <Step n={3}>BUSDC is the only sensible Pay token for shorts. Other tokens are swapped automatically.</Step>
      <Step n={4}>Set leverage, review, confirm.</Step>

      <h2>The Per-Side Adaptive Funding Rate</h2>
      <p>
        Synthetic markets run the same per-side adaptive funding mechanism described in the
        <Link to="/docs/liquidity"> Liquidity (MLP)</Link> section. In practical terms for traders:
      </p>
      <ul>
        <li>
          If long open interest dominates on a market (e.g., 80% long / 20% short on BTC), <strong>longs pay
          a funding fee that scales with the imbalance</strong>. The bigger the skew, the higher the rate.
        </li>
        <li>
          The under-represented side (shorts in that example) pays <strong>zero</strong> funding — they're
          getting paid by the market to balance the book.
        </li>
        <li>
          Funding accrues continuously and is debited from your collateral on close. You can see your
          accumulated funding in the position row's "Funding" column.
        </li>
        <li>
          This protects MLP holders from the v1 bleed pattern — see the Liquidity section for the full
          history of why this matters.
        </li>
      </ul>

      <h2>Closing a Synthetic Position</h2>
      <Step n={1}>Open the <strong>Positions</strong> tab below the chart.</Step>
      <Step n={2}>Click <strong>Close</strong> on the row.</Step>
      <Step n={3}>Choose to close a partial amount (drag slider) or the full position. Pick the token you
        want to receive — BUSDC is default for synths; you can swap to WBESC / MONEY / BESC at vault rates.</Step>
      <Step n={4}>Confirm. PnL settles instantly: collateral + profit (or − loss) − fees → your chosen receive token.</Step>

      <h2>Limit Orders, Stop-Loss & Take-Profit</h2>
      <p>
        Synthetic markets fully support limit orders, stop-loss, and take-profit triggers. Set them at
        position open (in the trade box) or any time after via the position row's <strong>Edit TP/SL</strong>
        button. Orders execute when the on-chain oracle price crosses the trigger. <strong>TWAP</strong> and{" "}
        <strong>Scale</strong> execution work on synthetic markets too — see the{" "}
        <Link to="/docs/order-types">Order Types</Link> section for all of them.
      </p>

      <h2>What You Cannot Do (Common Mistakes)</h2>
      <ul>
        <li>
          <strong>Cannot use BTC or ETH as collateral.</strong> Even though you trade BTC and ETH on MoneyX,
          there is no actual BTC / ETH in the vault — only BUSDC backs synthetic positions.
        </li>
        <li>
          <strong>Cannot open positions while at the OI cap.</strong> If a market is at "Max Long $1M" and
          someone else already filled it, you'll need to wait for a close before opening on that side.
          The "Max Long" / "Max Short" indicator in the trade box shows remaining capacity.
        </li>
        <li>
          <strong>Cannot trade stock markets when NYSE / NASDAQ is closed.</strong> Oracle stops updating,
          and the keeper will reject new opens / closes to prevent stale-price exploitation.
        </li>
        <li>
          <strong>Cannot withdraw collateral below maintenance margin.</strong> If your position is close to
          liquidation, the "Edit Collateral → Withdraw" path is blocked until you either reduce size or
          add more collateral.
        </li>
        <li>
          <strong>Cannot short BUSDC.</strong> Stable tokens are non-shortable by design.
        </li>
      </ul>

      <h2>Fees You'll See on a Synthetic Trade</h2>
      <DefTable rows={[
        ["Position fee",   "0.1% of position size — paid on open AND close. Goes to LP and stakers."],
        ["Swap fee",       "0.04%–0.3% only if your pay-with token ≠ BUSDC; depends on pool utilisation."],
        ["Funding fee",    "Per-side adaptive. 0 if you're on the under-represented side; scales with skew if dominant."],
        ["Execution fee",  "Small BESC tip to keepers for executing your market open/close (typical: $0.05–$0.10)."],
        ["Liquidation fee","$5 USD flat, paid from collateral to the liquidator. Only charged if you get liquidated."],
      ]} />
      <p>
        See the full <Link to="/docs/fees">Fees</Link> page for exact basis-point tables and the
        <Link to="/docs/fee-distribution"> Fee Distribution</Link> page for where every fee ends up.
      </p>
    </>
  );
}

// ─── Section: Contracts ───────────────────────────────────────────────────────

function SContracts() {
  return (
    <>
      <h1>Contract Addresses</h1>
      <p className="Docs-lead">
        All MoneyX contracts are deployed on <strong>BESC Hyperchain (Chain ID 2372)</strong>. Every address
        below is permanent and publicly auditable on the block explorer.
      </p>

      <Callout type="info">
        Verify any address at{" "}
        <ExternalLink href="https://explorer.beschyperchain.com">explorer.beschyperchain.com</ExternalLink>{" "}
        before interacting with it directly. Never interact with unverified contracts.
      </Callout>

      <h2>Core Protocol</h2>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["Vault",           "0xdB15dF10C2203291D9Fa218b487f924C3B017d6E", "Liquidity vault (SynthVault) — holds LP assets (BUSDC, WBESC, MONEY); counterparty to all trades"],
          ["Router",          "0x53b6C33Ba013594C1D12731FBce4Ae81cC348468", "Entry point for position opens/closes and swaps — most users interact here"],
          ["VaultReader",     "0x041F48A65d7C1a8f676c3A882043e8f46EB9B7C9", "Read-only: batch-fetch vault token data, pool amounts, prices"],
          ["Reader",          "0x4556ac6716D44E7EEad2d64486d88C073f2D96D0", "Read-only: positions, orders, reward info in one multicall"],
          ["MlpManager",      "0x3ccF047bEfDb42BA90544f0eA78f3B4fcDCb1b23", "SynthMlpManager — manages MLP minting/burning and fee routing to the pool"],
          ["RewardReader",    "0x4304709155a34D760f31a557D6012c93F6b6078b", "Read-only: batch-fetch staking/reward tracker balances"],
          ["OrderBook",       "0x9e47F6f5Acf48850a05a9f78dB14212495186E77", "Stores all pending limit, TP/SL, and trigger orders"],
          ["OrderBookReader", "0xD3250eBed7c0B21FF878b16F8Ae6D90552A547D4", "Read-only: batch-fetch open orders for an account"],
          ["PositionRouter",  "0xbA9Fb6386D4d0A033D5f14e3032b1E82e1A1b34f", "SynthPositionRouter — position increase/decrease request queue and keeper execution"],
          ["PositionManager", "0xe7cB7E1A2087A5990E1F56DCB211c867b4a53467", "Executes order book orders; manages keeper bot permissions"],
          ["Multicall",       "0x6dE3F6ae5a7Ba67Ec2cD12703a6Ce6F10Ca5EfEa", "Batches multiple view calls into one RPC request — used by the frontend"],
        ]}
      />

      <h2>Tokens</h2>
      <DataTable
        headers={["Token", "Address", "Description"]}
        rows={[
          ["MONEY",   "0xf794E0B0c0a91099ccAcac296193e04Fb860442a", "Governance and utility token. Stake to earn BUSDC fees + esMONEY"],
          ["esMONEY", "0xD26c352bb72308042cC069Bc432078bB5D9409c9", "Escrowed MONEY — earned as staking reward; vest over 1 year to unlock"],
          ["bnMONEY", "0xFB5E22863Ee836614d28F0E2f263deA015640fE8", "Bonus MONEY — Multiplier Points token that boosts staking APR"],
          ["MLP",     "0xFc80AF0eA8d75b34f52357310dFf81E47d6b08a0", "Liquidity Provider token (SynthMLP) — represents share of the MLP vault"],
          ["USDG",    "0x4368847c8c142Dd0077018629D27A47B9247E4D3", "Internal accounting stablecoin (SynthUSDG, not user-facing)"],
          ["WBESC",   "0x33e22F85CC1877697773ca5c85988663388883A0", "Wrapped BESC — ERC-20 wrapper for native BESC gas token"],
          ["BUSDC",   "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B", "Bridged USDC — primary collateral and settlement token on BESC"],
        ]}
      />

      <h2>Staking & Rewards</h2>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["RewardRouter",          "0x4EF6234261a112715c1f6D647d1873F1ad4d478C", "Main entry point for stake, unstake, claim, and compound operations"],
          ["StakedMoneyTracker",    "0x01ACB2d15B5F034796FaA95451c591b28132FA9d", "Tracks staked MONEY balances (sMONEY)"],
          ["BonusMoneyTracker",     "0x9F325D23D06d53ac26FDD26427374886b633FfF7", "Tracks staked MONEY + Bonus MPs (sbMONEY)"],
          ["FeeMoneyTracker",       "0x86bd10E06fda062aa743A14C53Be22CA304eFb25", "Distributes 25% of protocol fees to MONEY stakers (sbfMONEY)"],
          ["FeeMlpTracker",         "0xF251e5E2086046F8397BC521a38a6242eDfD6C94", "Distributes MLP staking rewards — BUSDC fees (fMLP)"],
          ["StakedMlpTracker",      "0x7D54031695509FddBf76D80252AcC8f76deA4224", "Tracks MLP staking for esMONEY rewards (fsMLP)"],
          ["StakedMoneyDistributor","0x840E64Ea4AF07aBCB13626054105A0f6378cFeA9", "Emits esMONEY rewards to MONEY stakers"],
          ["StakedMlpDistributor",  "0xd864e1A1982170f25100Bfbd04E367E660afE0Df", "Emits esMONEY rewards to MLP stakers"],
          ["MoneyVester",           "0x8c35409c69835E5f814e4d693d957af95Fc7F356", "Vests esMONEY → MONEY over 365 days (from MONEY staking)"],
          ["MlpVester",             "0x1ac51155569d30aB1a65D702e2f67554e3E21f92", "Vests esMONEY → MONEY over 365 days (from MLP holding)"],
        ]}
      />

      <h2>Referrals</h2>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["ReferralStorage", "0x8E5aBc8C223a7D7517913005F9f624D6479F762A", "Stores all referral codes, affiliate relationships, and tier data on-chain"],
          ["ReferralReader",  "0x22A10B3c7A5b7BA23A85a3b18095Aa048b7c9120", "Read-only: batch-fetch referral stats for an address"],
        ]}
      />

      <h2>SynthVault (Synthetic Markets)</h2>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["SynthVault",          "0xdB15dF10C2203291D9Fa218b487f924C3B017d6E", "BUSDC-only vault for synthetic long/short positions"],
          ["SynthRouter",         "0xD4BF1ffC2151a0cfd145be4B3Ecb535E005f8E53", "Router for synthetic market position opens/closes"],
          ["SynthPositionRouter", "0xbA9Fb6386D4d0A033D5f14e3032b1E82e1A1b34f", "Keeper queue for synthetic market order execution"],
          ["SynthVaultPriceFeed", "0xBCA974d8426680e530682ba2c4E9f72e159e2aa9", "Oracle price feed aggregator for all synthetic asset prices"],
          ["SynthMLP",            "0xFc80AF0eA8d75b34f52357310dFf81E47d6b08a0", "Synthetic market LP token — represents share of SynthVault pool"],
          ["SynthMlpManager",     "0x3ccF047bEfDb42BA90544f0eA78f3B4fcDCb1b23", "Manages SynthMLP minting/burning"],
          ["SynthUSDG",           "0x4368847c8c142Dd0077018629D27A47B9247E4D3", "Internal accounting token for SynthVault"],
          ["SynthShortsTracker",  "0x72635D2D42D3292c8118C9e6A68a105E8dAD260A", "Tracks global short OI across all synthetic markets"],
        ]}
      />

      <h2>Copy Trading</h2>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["CopyTradingStorage", "0xf330224aAdFfC2b7c48091eA5fb7702C7c61c339", "Stores trader registrations, follower relationships, and copy settings"],
          ["CopyTradingManager", "0x7c2354BAC8a4BEAbBd645c710761fB555cDC94ac", "Executes copy trades when a leader opens/closes a position"],
        ]}
      />

      <h2>v2 Upgrades — Adaptive Funding + Auto Fee Distribution</h2>
      <p>
        These contracts ship the two key improvements over v1: per-side skew-weighted funding (stops the
        LP bleed) and on-chain automated fee distribution (eliminates manual weekly admin runs).
      </p>
      <DataTable
        headers={["Contract", "Address", "Purpose"]}
        rows={[
          ["AdaptiveFundingLib",       "0xFB59481eCA66A291841f7F9860a5feA925e18caB", "Pure-math library: computes per-side funding accumulator from current OI skew. Used by both VaultUtils contracts."],
          ["VaultUtils (main)",        "0x42502D9F103f24094D2c1B5a4CFF33D125B52dc2", "Per-side adaptive funding state + fee math for the main Vault. Wired via Vault.setVaultUtils()."],
          ["SynthVaultUtils",          "0x049f615fCcc8418E71438F101560217626bE5Dad", "Same adaptive funding logic for SynthVault; overrides the OI source to read synth long sizes."],
          ["AutoFeeDistributor (main)","0x3FFC52BE6ff10086119815434BA35B917b7e49AF", "Sweeps BUSDC fees from main Vault, atomically splits 65/25/10 to MLP / MONEY stakers / treasury. Keeper-pokeable, no admin needed."],
          ["AutoFeeDistributor (synth)","0x75527D2FDd61Ce95deF119f724E1a2bBE708628d", "Same for SynthVault BUSDC fees."],
        ]}
      />

      <h2>Reading Contracts Directly</h2>
      <p>All contracts are standard Solidity EVM contracts. You can read any of them with:</p>
      <CodeBlock>{`// Ethers.js example
import { ethers } from 'ethers';
const provider = new ethers.JsonRpcProvider('https://rpc.beschyperchain.com');

// Read vault token info
const vaultReader = new ethers.Contract(
  '0x041F48A65d7C1a8f676c3A882043e8f46EB9B7C9',
  VaultReaderABI,
  provider
);
const info = await vaultReader.getVaultTokenInfoV4(
  '0xA5c71472b38afBd39cA5a0bBFBa5374CaEE03a2A', // Vault
  '0x86e39a9908A3E92AEa48F4a1b6A9258935383507', // PositionRouter
  '0x33e22F85CC1877697773ca5c85988663388883A0', // WBESC
  ethers.parseEther('1'),
  tokenAddresses
);`}</CodeBlock>
    </>
  );
}

// ─── Section: Notifications ───────────────────────────────────────────────────

function SNotifications() {
  return (
    <>
      <h1>Notifications & Alerts</h1>
      <p className="Docs-lead">
        Stay on top of your positions with price alerts, liquidation warnings, and automated monitoring
        using the MoneyX API and WebSocket price feed.
      </p>

      <h2>Browser Price Alerts</h2>
      <p>
        The MoneyX frontend supports browser notifications for price alerts when the tab is open. To enable:
      </p>
      <Step n={1}>Click the <strong>Settings ⚙️</strong> icon in the top-right corner.</Step>
      <Step n={2}>Enable <strong>Notifications</strong> in the settings panel.</Step>
      <Step n={3}>Your browser will ask for notification permission — click Allow.</Step>
      <Step n={4}>
        Create a price alert: on the trade page, right-click a price level on the chart, or use the
        alerts panel to set a target price.
      </Step>
      <Callout type="info">
        Browser notifications only work while the browser tab is open or running in the background.
        For persistent monitoring, use the WebSocket API or build a server-side bot.
      </Callout>

      <h2>WebSocket Price Feed</h2>
      <p>
        The MoneyX API provides a real-time WebSocket feed for all asset prices. Connect to receive
        sub-second price updates for building your own alerts and bots.
      </p>
      <CodeBlock>{`// JavaScript WebSocket client
const ws = new WebSocket('wss://api.moneyxpro.com/ws/prices');

ws.onopen = () => {
  console.log('Connected to MoneyX price feed');
};

ws.onmessage = (event) => {
  const prices = JSON.parse(event.data);
  // prices = { BTC: "67234.12", ETH: "3412.55", ... }

  const btcPrice = parseFloat(prices.BTC);
  if (btcPrice < 60000) {
    sendAlert('BTC below $60,000!');
  }
};

ws.onerror = (err) => console.error('WS error:', err);
ws.onclose = () => setTimeout(connect, 5000); // auto-reconnect`}</CodeBlock>

      <h2>Liquidation Warning Bot (Python)</h2>
      <p>
        Poll the REST API to check if any of your positions are approaching liquidation:
      </p>
      <CodeBlock>{`import requests, time

API_BASE = 'https://api.moneyxpro.com/api'
MY_ADDRESS = '0xYourWalletAddress'
WARN_THRESHOLD = 0.15  # warn when within 15% of liquidation

def check_positions():
    r = requests.get(f'{API_BASE}/positions/{MY_ADDRESS}')
    positions = r.json().get('positions', [])

    for pos in positions:
        liq_price = float(pos['liquidationPrice'])
        mark_price = float(pos['markPrice'])

        if pos['isLong']:
            distance = (mark_price - liq_price) / mark_price
        else:
            distance = (liq_price - mark_price) / mark_price

        if distance < WARN_THRESHOLD:
            print(f"WARNING: {pos['indexToken']} position near liquidation!")
            print(f"  Mark: \${mark_price:,.2f}  Liq: \${liq_price:,.2f}")
            # → send Telegram/Discord message here

while True:
    check_positions()
    time.sleep(30)  # check every 30 seconds`}</CodeBlock>

      <h2>Telegram Bot Integration</h2>
      <p>
        You can send alerts to a Telegram channel using the Telegram Bot API. Create a bot via
        <code>@BotFather</code> on Telegram, get a bot token, then add this to any monitoring script:
      </p>
      <CodeBlock>{`import requests

TELEGRAM_TOKEN = 'your-bot-token'
CHAT_ID = 'your-chat-id'

def send_telegram(message: str):
    url = f'https://api.telegram.org/bot{TELEGRAM_TOKEN}/sendMessage'
    requests.post(url, json={'chat_id': CHAT_ID, 'text': message})`}</CodeBlock>

      <h2>Webhook / Server-Side Alerts</h2>
      <p>
        For production-grade monitoring, run a Node.js or Python server that:
      </p>
      <ol>
        <li>Subscribes to the MoneyX WebSocket price feed</li>
        <li>Polls the REST API for your positions every 30–60 seconds</li>
        <li>Sends push notifications via Telegram, Discord webhook, email, or SMS when thresholds are hit</li>
        <li>Runs 24/7 independently of your browser session</li>
      </ol>
      <Callout type="tip">
        The REST API and WebSocket feed require no authentication for read-only price and public position
        data. For account-specific data, use API key auth — see <Link to="/docs/api-automation">API & Automation</Link>.
      </Callout>

      <h2>API Key Notifications Webhook</h2>
      <p>
        If you've created an API key on the <Link to="/api-keys">API Keys page</Link>, you can use it to
        access account-specific endpoints without connecting a wallet:
      </p>
      <CodeBlock>{`// Check your account's open positions via API key
const response = await fetch(
  'https://api.moneyxpro.com/api/account/positions/0xYourAddress',
  {
    headers: {
      'X-API-Key': 'your-api-key-here'
    }
  }
);
const data = await response.json();`}</CodeBlock>
    </>
  );
}

// ─── Section: API & Automation ────────────────────────────────────────────────

function SApiAutomation() {
  return (
    <>
      <h1>API & Automation</h1>
      <p className="Docs-lead">
        MoneyX exposes a full REST API and real-time WebSocket feed for building trading bots,
        analytics dashboards, and third-party integrations. See the full interactive reference at{" "}
        <Link to="/api-reference">API Reference</Link>.
      </p>

      <h2>Base URL</h2>
      <CodeBlock>{`REST:      https://api.moneyxpro.com/api
WebSocket: wss://api.moneyxpro.com/ws/prices`}</CodeBlock>

      <h2>Creating an API Key</h2>
      <Step n={1}>Go to <Link to="/api-keys">API Keys</Link> in the top nav.</Step>
      <Step n={2}>Connect your wallet.</Step>
      <Step n={3}>
        Click <strong>Generate API Key</strong>. Sign the EIP-191 message in your wallet to prove
        ownership — no transaction fee required.
      </Step>
      <Step n={4}>
        Copy your API key immediately — it is shown only once. The key is stored as a secure hash
        server-side. If you lose it, generate a new one.
      </Step>

      <Callout type="warn">
        Keep your API key secret. Anyone with your API key can access read-only account data linked to
        your address. API keys do not have permission to sign transactions or move funds.
      </Callout>

      <h2>Authentication</h2>
      <p>Pass your API key in the <code>X-API-Key</code> request header:</p>
      <CodeBlock>{`curl -H "X-API-Key: your-api-key" \\
     https://api.moneyxpro.com/api/account/stats/0xYourAddress`}</CodeBlock>

      <h2>Key Endpoints</h2>
      <DataTable
        headers={["Endpoint", "Method", "Description"]}
        rows={[
          ["/api/prices",                      "GET",    "Current prices for all supported assets"],
          ["/api/trades",                      "GET",    "All recent trades (paginated)"],
          ["/api/account/stats/:address",      "GET",    "Volume, PnL, trade count for an address"],
          ["/api/account/positions/:address",  "GET",    "Open positions for an address"],
          ["/api/account/orders/:address",     "GET",    "Open limit/TP/SL orders for an address"],
          ["/api/account/history/:address",    "GET",    "Closed trade history for an address"],
          ["/api/leaderboard",                 "GET",    "Top traders by volume/PnL (paginated)"],
          ["/api/referrals/:address",          "GET",    "Referral stats and earnings for an address"],
          ["/api/copy/leaderboard",            "GET",    "Registered copy trading leaders"],
          ["/api/stats",                       "GET",    "Platform-wide stats (TVL, volume, fees)"],
          ["/api/keys",                        "POST",   "Create a new API key (requires wallet signature)"],
          ["/ws/prices",                       "WS",     "Real-time price stream for all assets"],
        ]}
      />

      <h2>Example: Simple Trading Bot (JavaScript)</h2>
      <CodeBlock>{`import { ethers } from 'ethers';
import WebSocket from 'ws';

const PROVIDER_URL = 'https://rpc.beschyperchain.com';
const PRIVATE_KEY  = process.env.PRIVATE_KEY; // never hardcode!
const ROUTER_ADDR  = '0x53b6C33Ba013594C1D12731FBce4Ae81cC348468';

const provider = new ethers.JsonRpcProvider(PROVIDER_URL);
const signer   = new ethers.Wallet(PRIVATE_KEY, provider);
const router   = new ethers.Contract(ROUTER_ADDR, RouterABI, signer);

// 1. Watch live prices
const ws = new WebSocket('wss://api.moneyxpro.com/ws/prices');
ws.on('message', async (data) => {
  const prices = JSON.parse(data);
  const btcPrice = parseFloat(prices.BTC);

  // 2. Simple mean-reversion: buy when BTC dips below $60k
  if (btcPrice < 60_000) {
    console.log(\`BTC at $\${btcPrice} — opening long\`);

    // 3. Open a 10× BTC long via PositionRouter
    const tx = await router.increasePositionETH(
      [WBESC_ADDR],     // path
      BTC_ADDR,         // indexToken
      0,                // minOut
      ethers.parseEther('100'), // sizeDelta in USD (1e30)
      true,             // isLong
      btcPrice * 1.01 * 1e30, // acceptablePrice
      EXECUTION_FEE,
      ethers.ZeroHash,  // referralCode
      ethers.ZeroAddress
    );
    console.log('tx:', tx.hash);
  }
});`}</CodeBlock>
      <Callout type="warn">
        Never store private keys in code or environment variables in production. Use a hardware wallet,
        KMS (AWS/GCP), or a dedicated signing service for bot wallets.
      </Callout>

      <h2>Rate Limits</h2>
      <DataTable
        headers={["Tier", "Requests / minute", "Notes"]}
        rows={[
          ["Public (no key)",  "60",  "Price and public endpoints only"],
          ["API key",          "300", "Full access to account endpoints"],
          ["WebSocket",        "1 connection", "No rate limit on message frequency"],
        ]}
      />

      <h2>Full API Reference</h2>
      <p>
        For complete endpoint documentation with parameters, response schemas, and interactive curl
        examples, visit the <Link to="/api-reference">API Reference page</Link>.
      </p>
    </>
  );
}
